import { ehLinhaDeVoz } from '../../../../backend/src/services/whatsapp/gerar-mensagem-proposta';
/**
 * Componente Montador de Composição de Planos
 * Permite ao consultor montar a proposta linha por linha
 * Com cores Claro (vermelho/cinza) e 4 tipos: TT, PORT, NOVA, FIBRA
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRenovacaoParametros, type RenovacaoFaixaParametro } from '@/hooks/useRenovacaoParametros';
import { linhaPertenceAItem, type ItemPedidoMatch } from '@/lib/linhasProtegidasRevisao';
import { itemCanceladoDaLinha, linhaFicaCancelada } from '@/lib/ficaCanceladaRevisao';
import { CATEGORIA_M2M, CATEGORIAS_SERVICO_SOLTO, linhaEhServicoDigital, MENSAGEM_SERVICO_NA_LINHA } from '@/lib/servicoNaLinha';
import { useOperadoraLinhas } from '@/hooks/useOperadoraLinhas';
import { SeloOperadoraLinha } from './ConferenciaOperadoraLinhas';
import { regraPorTipoLinha } from '@/lib/operadoraCoerencia';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectContentMaxPriority } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  PLANOS_MOVEL_CNPJ as PLANOS_MOVEL_CNPJ_STATIC, 
  PLANOS_RENOVACAO_CNPJ as PLANOS_RENOVACAO_CNPJ_STATIC,
  PLANOS_PORTABILIDADE_CNPJ as PLANOS_PORTABILIDADE_CNPJ_STATIC,
  PLANOS_FIBRA as PLANOS_FIBRA_STATIC,
  PLANOS_TV as PLANOS_TV_STATIC,
  ADICIONAIS_TV as ADICIONAIS_TV_STATIC,
  ADICIONAIS_FIBRA as ADICIONAIS_FIBRA_STATIC,
  ADICIONAIS_MOVEL as ADICIONAIS_MOVEL_STATIC,
  getPlanoById,
  categoriaDoTipo, 
  calcularTotalGB, 
  calcularTotalValor, 
  formatarMoedaPlano,
  getTipoLinhaLabel,
  type PlanoCNPJ,
  type AdicionalMovel,
  type LinhaComposicao,
  type ComposicaoProposta,
  type AdicionalFibra,
  type AdicionalTV,
  type TipoBloco,
  type TipoLinha,
} from '@/constants/planosCNPJ';
import { usePlanosPropostaCatalogo, type PlanoCatalogoCategoria } from '@/hooks/usePlanosPropostaCatalogo';
import { useTiposProposta, corClassesTipo } from '@/hooks/useTiposProposta';
import { deltasDaRenovacao } from '@/lib/renovacaoCalculo';
import { tmPelaFidelidade } from '@/lib/fidelidadeConta';
import { tmDaRenovacao } from '@/lib/carteiraClaro/calcularAptidaoCarteira';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { BeneficiosPlano, BeneficiosResumo } from './BeneficiosPlano';
import { AparelhoSelectorLinha } from './AparelhoSelectorLinha';
import { formatarTelefone } from '@/utils/formatters';
import {
  Trash2,
  Copy,
  Phone,
  ArrowRightLeft, 
  Sparkles, 
  TrendingDown,
  Info,
  Star,
  Wifi,
  RefreshCw,
  PhoneOutgoing,
  Tv,
  Plus,
  Minus,
  BookOpen,
  Receipt,
  AlertCircle,
  LayoutList,
  LayoutGrid,
  Lock,
  Link2,
  ArrowRight,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { onlyDigits, formatPhone } from '@/utils/phone';
import { conferirFamiliaCpf, vagasDoTitular, chaveLinha, type MetaFamiliaCpf } from '../../../../backend/src/services/propostas/familia-cpf';
import { ModalRegrasRenovacao } from '@/components/carteira/fatura/ModalRegrasRenovacao';
import { ModalFaturaVisualizacao } from '@/components/carteira/fatura/ModalFaturaVisualizacao';

// Dados detalhados de cada linha da fatura
interface LinhaDetalhadaFatura {
  numero: string;
  gbAtual: number;
  valorAtual: number;
  planoAtual: string;
  dataFidelidade?: string | null;
}

interface ItemVinculadoRevisao extends ItemPedidoMatch {
  _numeroItem?: string | null;
}

interface MontadorComposicaoPlanoProps {
  tipoPessoa?: "PF" | "PJ";
  clienteId?: string; // Para modal de fatura
  razaoSocial?: string; // Para exibir no modal de fatura
  linhasIniciais?: string[]; // Números de telefone do plano atual (são TT ou RENOVACAO)
  linhasDetalhadas?: LinhaDetalhadaFatura[]; // Dados completos das linhas da fatura
  valorAtual?: number;
  composicaoExterna?: ComposicaoProposta | null; // Para receber de cenários
  onComposicaoChange?: (composicao: ComposicaoProposta) => void;
  onValoresChange?: (valores: { linhas: number; gb: number; valor: number }) => void;
  tipoLinhaInicial?: TipoLinha; // Tipo base das linhas: 'TT' para CPF→CNPJ, 'RENOVACAO' para CNPJ→CNPJ
  qtdLinhasIniciais?: number; // Quantidade de linhas a criar mesmo sem números (para placeholder)
  propostaStatus?: string | null; // ✅ (A) Status da proposta para modo read-only
  linhasTravadasIds?: Set<string>; // Linhas FINALIZADAS na revisão de cancelado — não editáveis. (Douglas 16/06)
  itensProtegidosRevisao?: ItemPedidoMatch[]; // Itens protegidos (não-cancelados) p/ trava robusta por linha_id OU telefone. (Douglas 16/06)
  itensVinculadosRevisao?: ItemVinculadoRevisao[]; // TODOS os itens do pedido (mãe+filhos) c/ _numeroItem — sinaliza vínculo e trava EXCLUSÃO (não a edição). (Douglas 22/06)
  /** Revisão de pedido CANCELADO: libera trocar o produto da linha dentro da mesma família. (Douglas 03/08) */
  revisaoDeCancelado?: boolean;
  /** Recuperar parte do pedido (Douglas 23/10/2026): itens cancelados que o consultor deixa no cancelado. */
  ficamCanceladosIds?: ReadonlySet<string>;
  onFicamCanceladosChange?: (ids: Set<string>) => void;
  modoAmplo?: boolean;
  /** TM da carteira Claro do CNPJ: decide aparelho na renovação total */
  tmCarteiraRenovacao?: number | null;
}

// Cores Claro
const CLARO_RED = 'bg-[#E31937] hover:bg-[#c71530] text-white';
const CLARO_RED_LIGHT = 'bg-danger/5 border-danger/40';
const CLARO_GRAY = 'bg-muted hover:bg-muted-foreground text-foreground';

// Cores por tipo de linha
const TIPO_CORES: Record<TipoLinha, { bg: string; text: string; border: string }> = {
  TT: { bg: 'bg-danger/10', text: 'text-danger', border: 'border-danger' },
  TT_PRE: { bg: 'bg-primary/[0.10]', text: 'text-primary dark:text-foreground', border: 'border-primary/30' },
  PORTABILIDADE: { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning' },
  NOVA: { bg: 'bg-success/10', text: 'text-success', border: 'border-success' },
  FIBRA: { bg: 'bg-info/10', text: 'text-info', border: 'border-info' },
  TV: { bg: 'bg-primary/[0.10]', text: 'text-primary dark:text-foreground', border: 'border-primary/30' },
  RENOVACAO: { bg: 'bg-info/10', text: 'text-info', border: 'border-info' },
  LINHA_NOVA: { bg: 'bg-success/10', text: 'text-success', border: 'border-success' },
  SERVICO: { bg: 'bg-primary/[0.10]', text: 'text-primary dark:text-foreground', border: 'border-primary/30' },
};

// 67/Proposta — a cor sai do fundo e vai para o filete de 4px: degradê vinho
// em renovação/TT/portabilidade, verde só na linha NOVA (único desfecho-novo),
// neutro para fibra/TV/serviço. O chip do tipo vira contorno + dot na mesma cor.
const ehLinhaNova = (tipo: TipoLinha) => tipo === 'NOVA' || tipo === 'LINHA_NOVA';
const ehLinhaVinho = (tipo: TipoLinha) => tipo === 'TT' || tipo === 'TT_PRE' || tipo === 'RENOVACAO' || tipo === 'PORTABILIDADE';
const fileteDaLinha = (tipo: TipoLinha): string =>
  ehLinhaNova(tipo)
    ? 'bg-[#075E54] dark:bg-[#25D366]'
    : ehLinhaVinho(tipo)
      ? 'bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]'
      : 'bg-[#D8CFC2] dark:bg-[#3D2F2F]';
const dotDaLinha = (tipo: TipoLinha): string =>
  ehLinhaNova(tipo)
    ? 'bg-[#075E54] dark:bg-[#25D366]'
    : ehLinhaVinho(tipo)
      ? 'bg-primary dark:bg-[#C87484]'
      : 'bg-muted-foreground/50';
const CHIP_TIPO_NEUTRO = 'border-border bg-card text-[9.5px] font-bold text-foreground';

const TIPO_ICONES: Record<TipoLinha, React.ReactNode> = {
  TT: <RefreshCw className="w-3 h-3" />,
  TT_PRE: <RefreshCw className="w-3 h-3" />,
  PORTABILIDADE: <ArrowRightLeft className="w-3 h-3" />,
  NOVA: <Sparkles className="w-3 h-3" />,
  FIBRA: <Wifi className="w-3 h-3" />,
  TV: <Tv className="w-3 h-3" />,
  RENOVACAO: <RefreshCw className="w-3 h-3" />,
  LINHA_NOVA: <PhoneOutgoing className="w-3 h-3" />,
  SERVICO: <Sparkles className="w-3 h-3" />,
};

// Defaults para planos NOVOS (criados via banco/UI) sem correspondente no estático:
// garante beneficios:[] (BeneficiosPlano lê .includes/.map) e um tipo válido.
const DEFAULTS_MOVEL = { beneficios: [] as string[], tipo: 'MOVEL' as const };
const DEFAULTS_FIBRA = { beneficios: [] as string[], tipo: 'FIBRA' as const };
const DEFAULTS_TV = { beneficios: [] as string[], tipo: 'COMBO' as const };
const BUILTIN_CATS = new Set([
  'tt_pos', 'tt_pre', 'pn_novo', 'nova', 'renov', 'bloco_novo', 'bloco_incr', 'bloco_renov',
  'movel_migracao', 'movel_migracao_pre', 'movel_renovacao', 'movel_portabilidade',
  'movel_bloco', 'fibra', 'tv', 'combo', 'adicional_tv', 'adicional_fibra', 'adicional_movel',
  // Convergente não é produto: móvel e fibra entram como linhas separadas (Douglas 15/09).
  'convergente',
  // ⛔ 'aparelhos' TEM que estar aqui. Ela e' a UNICA categoria do catalogo sem
  // registro em `proposta_tipos`, e `tipoRealDaCategoria` (~572) devolve 'SERVICO'
  // para tudo que nao reconhece — um fallback que falha ABERTO. Sem esta linha a
  // categoria caia em `categoriasCustom`, entrava em `opcoesServicoDigital` e os
  // 137 aparelhos apareciam na faixa SERVICOS da linha (ordem alfabetica poe
  // 'aparelhos' primeiro, entao os 2 chips sugeridos eram iPhone).
  // O estrago nao era cosmetico: clicar no chip cria linha `tipo:'SERVICO'`, cujo
  // valor entra como MENSALIDADE vezes o numero de "licencas". Gerou o item
  // IPHONE 17 256GB com quantidade 24 e subtotal R$ 147.576 no pedido 2608120032.
  // Aparelho se escolhe DENTRO da linha (AparelhoSelectorLinha), nunca como item
  // avulso. (Douglas 02/09/2026)
  'aparelhos',
]);

const chaveAdicionais = (adicionais?: Record<string, number>) =>
  Object.entries(adicionais || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([codigo, quantidade]) => `${codigo}:${quantidade}`)
    .join('-');

const chaveLinhasComposicao = (linhas: LinhaComposicao[]) => linhas.map((linha) => [
  linha.id,
  linha.linha_id,
  linha.tipo,
  linha.numero,
  linha.planoId,
  linha.plano?.id,
  linha.plano?.nome,
  linha.plano?.valor,
  linha.planoAtual,
  linha.valorAtual,
  linha.gbAtual,
  linha.planoNovo,
  linha.receitaRenovada,
  linha.receitaAdicionada,
  linha.ticketNovo,
  linha.tipoFibra,
  linha.tipoBloco,
  linha.categoriaCustom,
  linha.quantidade,
  linha.linhaVinculada,
  linha.aparelho?.slug,
  linha.aparelho?.forma,
  chaveAdicionais(linha.adicionais),
].map((valor) => valor ?? '').join('|')).join(',');


// Campo de quantidade do lote — fora do render de propósito (ver comentário no
// uso). Digitação própria, igual ao telefone: o valor de fora só entra quando o
// campo não está com o cursor, senão o clamp reescreve o que está sendo digitado.
const CampoQuantidadeLote = React.memo(function CampoQuantidadeLote({
  valor, onChange, className, disabled,
}: { valor: number; onChange: (n: number) => void; className?: string; disabled?: boolean }) {
  const [rascunho, setRascunho] = useState(String(valor));
  const focadoRef = useRef(false);
  useEffect(() => {
    if (!focadoRef.current) setRascunho(String(valor));
  }, [valor]);
  const commit = (texto: string) => {
    const n = Math.min(200, Math.max(1, Number(texto) || 1));
    setRascunho(String(n));
    onChange(n);
  };
  return (
    <input
      type="number" min={1} max={200}
      value={rascunho}
      onFocus={() => { focadoRef.current = true; }}
      onChange={(e) => {
        setRascunho(e.target.value);
        const n = Number(e.target.value);
        if (Number.isFinite(n) && n >= 1 && n <= 200) onChange(n);
      }}
      onBlur={(e) => { focadoRef.current = false; commit(e.target.value); }}
      className={className}
      disabled={disabled}
    />
  );
});


// ── "+ MAIS" DOS SERVIÇOS DIGITAIS (Douglas 01/09/2026) ───────────────────────
// São muitos serviços e o admin cria mais quando quer: uma parede de chips não
// serve. Aqui é lista com busca, agrupada pela família configurada em Planos &
// Preços, com o valor de cada um à vista.
const SeletorServicoDigital = React.memo(function SeletorServicoDigital({
  opcoes,
  ativos,
  nomeCategoria,
  onEscolher,
}: {
  opcoes: Array<{ categoria: string; plano: PlanoCNPJ }>;
  ativos: Set<string | null | undefined>;
  nomeCategoria: (categoria: string) => string;
  onEscolher: (opcao: { categoria: string; plano: PlanoCNPJ }) => void;
}) {
  const [busca, setBusca] = useState('');
  const filtradas = opcoes.filter((o) =>
    o.plano.nome.toLowerCase().includes(busca.trim().toLowerCase()));
  const porCategoria = new Map<string, Array<{ categoria: string; plano: PlanoCNPJ }>>();
  for (const o of filtradas) porCategoria.set(o.categoria, [...(porCategoria.get(o.categoria) || []), o]);

  return (
    <div className="space-y-2">
      <Input value={busca} onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar serviço" className="h-8 text-xs" />
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {[...porCategoria.entries()].map(([categoria, itens]) => (
          <div key={categoria}>
            <p className="px-1 pb-1 text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              {nomeCategoria(categoria)}
            </p>
            {itens.map((o) => (
              <button key={o.plano.id} type="button" onClick={() => onEscolher(o)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-xs transition-colors hover:bg-muted',
                  ativos.has(o.plano.id) && 'bg-success/10 text-success',
                )}>
                <span className="flex-1 truncate">{o.plano.nome}</span>
                <span className="tabular-nums text-muted-foreground">{formatarMoedaPlano(o.plano.valor)}</span>
                <span className="text-muted-foreground">{ativos.has(o.plano.id) ? 'incluído' : '+'}</span>
              </button>
            ))}
          </div>
        ))}
        {filtradas.length === 0 && (
          <p className="px-1 py-2 text-xs text-muted-foreground">nenhum serviço com esse nome</p>
        )}
      </div>
    </div>
  );
});


// ── TELEFONE DA LINHA — digitação própria (Douglas 01/09/2026) ────────────────
// O campo guardava o número direto no estado da composição: cada tecla subia pro
// modal, voltava como `composicaoExterna` e reescrevia a lista de linhas. Quando
// o eco chegava atrasado (o modal re-renderiza inteiro a cada tecla), ele trazia
// o valor de duas teclas atrás e a digitação ANDAVA PARA TRÁS — foi assim que o
// lead L1086618 gravou "51995" e o consultor não conseguia terminar o número.
// Agora o que se digita mora aqui dentro; sobe no blur (ou 600ms parado), e o
// valor de fora só entra quando o campo NÃO está com o cursor.
const InputTelefoneLinha = React.memo(function InputTelefoneLinha({
  valor,
  onCommit,
  disabled,
  className,
  placeholder = '(00) 00000-0000',
  title,
}: {
  valor: string;
  onCommit: (valor: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  title?: string;
}) {
  const [rascunho, setRascunho] = useState(valor);
  const focadoRef = useRef(false);
  const valorRef = useRef(valor);
  valorRef.current = valor;
  // onCommit chega novo a cada render do montador; se ele entrasse na dependência
  // do timer, cada re-render reiniciaria os 600ms e o número nunca subiria.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    if (!focadoRef.current) setRascunho(valor);
  }, [valor]);

  useEffect(() => {
    if (rascunho === valorRef.current) return;
    const t = setTimeout(() => onCommitRef.current(rascunho), 600);
    return () => clearTimeout(t);
  }, [rascunho]);

  // Rede: se o campo sair da tela com o que foi digitado ainda pendente (modal
  // fechado, aba trocada, linha removida), o número sobe antes de morrer. O
  // clique em "Criar pedido" já dispara o blur antes do click — isto é para o
  // resto.
  const rascunhoRef = useRef(rascunho);
  rascunhoRef.current = rascunho;
  useEffect(() => () => {
    if (rascunhoRef.current !== valorRef.current) onCommitRef.current(rascunhoRef.current);
  }, []);

  return (
    <Input
      value={rascunho}
      onChange={(e) => setRascunho(e.target.value)}
      onFocus={() => { focadoRef.current = true; }}
      onBlur={(e) => {
        focadoRef.current = false;
        if (e.target.value !== valorRef.current) onCommit(e.target.value);
      }}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      title={title}
    />
  );
});


// Vazios ESTÁVEIS: `= []` no default do parâmetro cria array novo a cada render
// e reacende os efeitos que dependem dele (o de inicialização, entre outros).
// (Douglas 02/09/2026)
const VAZIO_LINHAS: string[] = [];
const VAZIO_DETALHADAS: LinhaDetalhadaFatura[] = [];
const VAZIO_ITENS: ItemPedidoMatch[] = [];
const VAZIO_TRAVADAS: Set<string> = new Set();
const VAZIO_FICAM: ReadonlySet<string> = new Set();

export const MontadorComposicaoPlano = React.memo(function MontadorComposicaoPlano({
  clienteId,
  razaoSocial,
  linhasIniciais = VAZIO_LINHAS,
  linhasDetalhadas = VAZIO_DETALHADAS,
  valorAtual = 0,
  composicaoExterna,
  onComposicaoChange,
  onValoresChange,
  tipoLinhaInicial = 'TT', // Default TT para leads CPF→CNPJ
  qtdLinhasIniciais = 0, // Quantidade de linhas placeholder (se não tiver números)
  propostaStatus = 'RASCUNHO', // ✅ (A) Default RASCUNHO
  linhasTravadasIds = VAZIO_TRAVADAS, // Linhas finalizadas (revisão de cancelado). (Douglas 16/06)
  itensProtegidosRevisao = VAZIO_ITENS, // Itens protegidos p/ trava robusta por linha. (Douglas 16/06)
  itensVinculadosRevisao = VAZIO_ITENS, // Itens vinculados ao pedido — sinalização + trava de exclusão. (Douglas 22/06)
  revisaoDeCancelado = false, // Troca de produto na revisão de cancelado. (Douglas 03/08)
  ficamCanceladosIds = VAZIO_FICAM,
  onFicamCanceladosChange,
  modoAmplo = false,
  tmCarteiraRenovacao = null,
  tipoPessoa = "PJ",
}: MontadorComposicaoPlanoProps) {
  // ✅ (A) Determinar se está em modo read-only
  const isReadOnly = ['CONVERTIDA', 'CONVERTIDA_EM_PEDIDO'].includes(String(propostaStatus || '').trim().toUpperCase());
  // Trava por LINHA (revisão de cancelado): a linha finalizada não pode ser editada/removida. (Douglas 16/06)
  const isLinhaTravada = (linha: LinhaComposicao): boolean => {
    if (!!linha?.linha_id && !!linhasTravadasIds?.has(linha.linha_id)) return true;
    // ROBUSTO (Douglas 16/06): casa a linha RENDERIZADA contra os itens protegidos
    // (não-cancelados) por linha_id OU telefone — NAO depende do composicaoBase.
    return (itensProtegidosRevisao || []).some((item) => linhaPertenceAItem(linha, item));
  };
  // Vínculo com item do pedido na revisão: a linha JÁ é um item do pedido. Pode
  // EDITAR (reflete no item) mas NÃO pode EXCLUIR — apagar quebraria/órfãaria o
  // item da planilha. (Douglas 22/06: "editar ou deixar como está, nunca apagar;
  // só se realmente vinculado".)
  const getItemVinculado = (linha: LinhaComposicao): ItemVinculadoRevisao | null =>
    (itensVinculadosRevisao || []).find((item) => linhaPertenceAItem(linha, item)) || null;
  const isLinhaVinculada = (linha: LinhaComposicao): boolean => !!getItemVinculado(linha);
  const numeroItemDaLinha = (linha: LinhaComposicao): string | null => getItemVinculado(linha)?._numeroItem || null;
  // (Douglas 03/08) Campo travado só ficava inerte — parecia tela quebrada.
  // Agora o motivo aparece no hover, com a mesma frase em todos os controles.
  const MOTIVO_TRAVA = 'Item já finalizado — na recuperação só o item cancelado pode ser editado.';
  // Recuperar parte do pedido (Douglas 23/10/2026): a linha que o cliente não quer
  // mais fica no cancelado. Só item cancelado entra; a marca é do item, então um
  // item agrupado leva todas as suas linhas e o serviço segue a linha dona.
  const podeFicarCancelada = revisaoDeCancelado && !isReadOnly && !!onFicamCanceladosChange;
  const itemQueFicaDaLinha = (linha: LinhaComposicao) =>
    podeFicarCancelada ? itemCanceladoDaLinha(linha, itensVinculadosRevisao || []) : null;
  const ficaCancelada = (linha: LinhaComposicao): boolean =>
    podeFicarCancelada && linhaFicaCancelada(linha, linhas, itensVinculadosRevisao || [], ficamCanceladosIds);
  const motivoTrava = (linha: LinhaComposicao) =>
    ficaCancelada(linha) ? 'Fica cancelada — clique em Volta para trazer de novo ao pedido.' : MOTIVO_TRAVA;
  const marcarFicaCancelada = (alvo: LinhaComposicao[], fica: boolean) => {
    if (!onFicamCanceladosChange) return;
    const ids = new Set(ficamCanceladosIds);
    for (const linha of alvo) {
      const item = itemQueFicaDaLinha(linha);
      if (!item?.id) continue;
      if (fica) ids.add(item.id); else ids.delete(item.id);
    }
    onFicamCanceladosChange(ids);
  };
  const renderAcaoFicaCancelada = (linha: LinhaComposicao, compacto: boolean) => {
    const item = itemQueFicaDaLinha(linha);
    if (!item?.id) return null;
    const fica = ficaCancelada(linha);
    if (compacto && !fica) return null;
    if (fica && !ficamCanceladosIds.has(item.id)) return null;
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={`h-[24px] shrink-0 gap-1 rounded-full px-2 text-[10.5px] ${fica
          ? 'border-[#E4DDD3] bg-white text-foreground dark:border-border dark:bg-transparent'
          : 'border-[#D9B8BF] text-[#7A2235] hover:text-[#7A2235] dark:border-[#5E2229] dark:text-[#C87484]'}`}
        title={fica ? 'Volta para o pedido recuperado' : 'O cliente não quer esta linha: ela fica no cancelado'}
        onClick={() => marcarFicaCancelada([linha], !fica)}
      >
        {fica ? <RotateCcw className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
        {fica ? 'Volta' : 'Deixar cancelada'}
      </Button>
    );
  };
  // Selo da linha na revisão: 🔒 finalizado (não edita/exclui) OU 🔗 vinculado (edita, não exclui).
  const renderVinculoBadge = (linha: LinhaComposicao) => {
    const travadaB = isLinhaTravada(linha);
    const num = numeroItemDaLinha(linha);
    const fica = ficaCancelada(linha);
    if (fica) {
      return (
        <span className="inline-flex flex-wrap items-center gap-1 shrink-0">
          {num && <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5"><Link2 className="h-3 w-3" /> {num}</span>}
          <span className="rounded-full border border-[#D9B8BF] bg-[#F7EEEF] px-1.5 py-px text-[10px] font-semibold text-[#7A2235] dark:border-[#5E2229] dark:bg-[#2A1F1F] dark:text-[#C87484]">FICA CANCELADA</span>
        </span>
      );
    }
    if (travadaB) {
      return <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 shrink-0" title={`${num ? `Item ${num} — ` : ''}finalizado, não editável`}><Lock className="h-3 w-3" /> {num || 'finalizado'}</span>;
    }
    if (num) {
      return <span className="text-[10px] text-info inline-flex items-center gap-0.5 shrink-0" title="Vinculado a item do pedido — edite ou mantenha (não exclui)"><Link2 className="h-3 w-3" /> {num}</span>;
    }
    return null;
  };
  const [linhas, setLinhas] = useState<LinhaComposicao[]>([]);
  const temLinhaRenovacao = linhas.some((linha) => linha.tipo === 'RENOVACAO');
  // ⛔ Douglas 04/09/2026: "não fala de qual mês é a fatura". A barra tinha
  // botão "Fatura" (abre o PDF), mas nada dizia SE a proposta estava presa a
  // uma fatura real, nem de qual mês — o consultor só descobria que faltava o
  // vínculo ao tentar Criar Pedido e levar o "importe a conta/fatura do
  // cliente". Aviso cedo em vez de tarde.
  const faturaVinculadaRenovacao = composicaoExterna?.renovacao?.faturaUploadId || null;
  const mesCompetenciaRenovacaoBruto = composicaoExterna?.renovacao?.mesCompetencia;
  const mesCompetenciaRenovacao = mesCompetenciaRenovacaoBruto
    || new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' })
      .format(new Date());
  const mesCompetenciaRenovacaoLabel = mesCompetenciaRenovacaoBruto
    ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', month: 'short', year: 'numeric' })
        .format(new Date(`${mesCompetenciaRenovacaoBruto}-01T12:00:00`))
        .replace('.', '')
    : null;
  const { data: parametrosRenovacao } = useRenovacaoParametros(
    temLinhaRenovacao ? mesCompetenciaRenovacao : '',
  );
  // Operadora real das linhas com régua (PORTABILIDADE/TT/RENOVAÇÃO) — selo
  // sutil dentro do quadrante de cada linha (Douglas 07/08). Cache 60d grátis;
  // o que faltar vai na consulta oficial. Sem resposta = sem selo, sem trava.
  const numerosParaOperadora = useMemo(
    () => linhas
      .filter((l) => regraPorTipoLinha(l?.tipo))
      .map((l) => String(l?.numero ?? ''))
      .filter((n) => n.replace(/\D/g, '').length >= 10),
    [linhas],
  );
  const { porTelefone: operadorasLinhas, consultando: consultandoOperadora } =
    useOperadoraLinhas(numerosParaOperadora);
  const inicializouRef = useRef(false);
  const ultimaComposicaoRef = useRef<string>('');
  // Chave da ULTIMA composicao externa que este montador adotou. E' o que
  // distingue "o modal mandou coisa nova" de "o modal ainda nao ouviu a minha
  // ultima mudanca" — sem isso o efeito de sincronizacao desfaz o item que o
  // consultor acabou de adicionar. Ver o comentario do efeito la' embaixo.
  const ultimaExternaRef = useRef<string | null>(null);
  const linhasAtuaisRef = useRef<LinhaComposicao[]>(linhas);
  useEffect(() => {
    linhasAtuaisRef.current = linhas;
  }, [linhas]);
  // O que ESTE montador acabou de mandar pro modal. O modal devolve a composição
  // como `composicaoExterna`, e o retorno chega um render depois — aplicado por
  // cima do que o consultor está digitando, ele repõe o estado anterior e a
  // digitação ANDA PARA TRÁS (lead L1086618 gravou "51995"). Eco recente é
  // ignorado; a janela é curta de propósito, para que sincronização legítima de
  // fora (hidratação, cenário, fatura) continue entrando. (Douglas 01/09/2026)
  const chavesEmitidasRef = useRef<Map<string, number>>(new Map());
  const JANELA_ECO_MS = 2_000;

  // ============= Catálogo dinâmico (DB-driven, com fallback estático) =============
  // Fonte = catálogo do banco (planos_proposta_catalogo). O estático entra só como
  // enriquecimento (benefícios, megabônus, maxLinhasBan...) e como fallback se o banco
  // estiver vazio/indisponível. Assim, plano novo criado na aba Planos & Preços aparece
  // aqui sozinho. Snapshot histórico: pedidos já criados NÃO mudam (guardam valor próprio).
  const { withOverrides, withCatalogo, rows: catalogoRows, planoDependenteCpf } = usePlanosPropostaCatalogo(tipoPessoa);
  const { byCategoria: tiposByCategoria } = useTiposProposta();
  const PLANOS_MOVEL_CNPJ = useMemo(
    () => withCatalogo('movel_migracao', PLANOS_MOVEL_CNPJ_STATIC.filter(p => !p.id.startsWith('bloco-')), DEFAULTS_MOVEL)
      .concat(withCatalogo('movel_bloco', PLANOS_MOVEL_CNPJ_STATIC.filter(p => p.id.startsWith('bloco-')), DEFAULTS_MOVEL)),
    [withCatalogo],
  );
  // TT PRE-PJ: agora vem das linhas movel_migracao_pre do banco (enriquecidas por GB pelo Pós).
  const PLANOS_MOVEL_CNPJ_PRE = useMemo(
    () => withCatalogo('movel_migracao_pre', PLANOS_MOVEL_CNPJ_STATIC.filter(p => !p.id.startsWith('bloco-')), DEFAULTS_MOVEL),
    [withCatalogo],
  );
  const PLANOS_RENOVACAO_CNPJ = useMemo(
    () => withCatalogo('movel_renovacao', PLANOS_RENOVACAO_CNPJ_STATIC, DEFAULTS_MOVEL),
    [withCatalogo],
  );
  const PLANOS_PORTABILIDADE_CNPJ = useMemo(
    () => withCatalogo('movel_portabilidade', PLANOS_PORTABILIDADE_CNPJ_STATIC, DEFAULTS_MOVEL),
    [withCatalogo],
  );
  const PLANOS_FIBRA = useMemo(
    () => withCatalogo('fibra', PLANOS_FIBRA_STATIC, DEFAULTS_FIBRA),
    [withCatalogo],
  );
  const PLANOS_TV = useMemo(
    () => withCatalogo('tv', PLANOS_TV_STATIC, DEFAULTS_TV),
    [withCatalogo],
  );
  const ADICIONAIS_TV = useMemo(
    () => tipoPessoa === 'PF'
      ? withCatalogo<AdicionalTV>('adicional_tv', [])
      : withOverrides<AdicionalTV>('adicional_tv', ADICIONAIS_TV_STATIC.filter((a) => !a.id.startsWith('cpf-'))),
    [withOverrides, withCatalogo, tipoPessoa],
  );
  const ADICIONAIS_FIBRA = useMemo(
    () => tipoPessoa === 'PF'
      ? withCatalogo<AdicionalFibra>('adicional_fibra', [])
      : withOverrides<AdicionalFibra>('adicional_fibra', ADICIONAIS_FIBRA_STATIC.filter((a) => !a.id.startsWith('cpf-'))),
    [withOverrides, withCatalogo, tipoPessoa],
  );
  const ADICIONAIS_MOVEL = useMemo(
    () => withCatalogo<AdicionalMovel & { produtoPedidoId?: string | null }>(
      'adicional_movel',
      ADICIONAIS_MOVEL_STATIC,
    )
      .map((adicional) => ({
        ...adicional,
        produtoId: adicional.produtoPedidoId ?? adicional.produtoId,
      }))
      .filter((adicional) => Boolean(adicional.produtoId)),
    [withCatalogo],
  );

  // Cada tipo lê a SUA categoria no catálogo (registro proposta_tipos) — 1:1 com a proposta.
  // Delega para a regua UNICA (constants/planosCNPJ.ts). Havia uma copia aqui; com
  // duas, o produto do pedido passava a depender de qual tela criou a linha.
  const catDoTipo = (tipo: TipoLinha, tipoBloco?: string, categoriaCustom?: string): string =>
    categoriaDoTipo(tipo, tipoBloco, categoriaCustom);
  const estaticosDaCat = (cat: string) => {
    const semBloco = PLANOS_MOVEL_CNPJ_STATIC.filter((p) => !p.id.startsWith('bloco-'));
    const soBloco = PLANOS_MOVEL_CNPJ_STATIC.filter((p) => p.id.startsWith('bloco-'));
    switch (cat) {
      case 'tt_pos': case 'nova': case 'tt_pre': return semBloco;
      case 'pn_novo': case 'pn_incremento': return PLANOS_PORTABILIDADE_CNPJ_STATIC;
      case 'renov': return PLANOS_RENOVACAO_CNPJ_STATIC;
      case 'bloco_novo': case 'bloco_incr': case 'bloco_renov': return soBloco;
      case 'fibra': return PLANOS_FIBRA_STATIC;
      case 'tv': return PLANOS_TV_STATIC;
      default: return [];
    }
  };
  // Planos da categoria do tipo (catálogo do banco; estático = enriquecimento/fallback).
  const ehCpf = tipoPessoa === "PF";
  const promoPlanoCpf = (plano?: PlanoCNPJ) => {
    const meses = Number(plano?.mesesPromocionais || 0);
    const depois = Number(plano?.valorAposPromocao || 0);
    return ehCpf && meses && depois ? ` · ${meses} meses, depois ${formatarMoedaPlano(depois)}` : '';
  };
  // Douglas 15/09/2026: o valor quem escolhe é o consultor. A praça só APARECE
  // (GM ou MD), sem esconder plano nem travar escolha.
  const pracaPlanoCpf = (plano?: PlanoCNPJ) => {
    const praca = String(plano?.praca || '').toUpperCase();
    return ehCpf && (praca === 'GM' || praca === 'MD') ? ` · ${praca}` : '';
  };
  const getPlanosPorTipo = (tipo: TipoLinha, categoriaCustom?: string, tipoBloco?: string): PlanoCNPJ[] => {
    const cat = catDoTipo(tipo, tipoBloco, categoriaCustom);
    if (!cat) return [];
    const defaults = cat === 'fibra' ? DEFAULTS_FIBRA : cat === 'tv' ? DEFAULTS_TV : DEFAULTS_MOVEL;
    return withCatalogo<PlanoCNPJ>(cat as PlanoCatalogoCategoria, estaticosDaCat(cat) as PlanoCNPJ[], defaults);
  };

  // ── FAMÍLIA CPF (Douglas 29/09/2026) ─────────────────────────────────────
  // "ele não pode comprar duas linhas de 40GB 49,90, o sistema não permite. Então
  //  teremos que vender o plano com dependentes." A régua é a mesma que trava o
  // pedido (familia-cpf.ts): o mesmo plano não entra duas vezes; dependente só
  // num Pós, incluídos primeiro (R$ 0,00), depois pagos, até o máximo do plano.
  const metaDoPlano = (plano?: PlanoCNPJ): MetaFamiliaCpf | null => plano?.familiaMovel ? {
    familia_movel: plano.familiaMovel, plano_claro: plano.planoClaro,
    dependentes_inclusos: plano.dependentesInclusos, dependentes_pagos_max: plano.dependentesPagosMax,
    dependente: plano.dependente, modalidade: plano.modalidade,
  } : null;
  const familiaCpf = useMemo(
    () => (ehCpf ? conferirFamiliaCpf(linhas, (l) => metaDoPlano((l as LinhaComposicao).plano)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linhas, ehCpf],
  );
  const TIPOS_DEPENDENTE: TipoLinha[] = ['PORTABILIDADE', 'RENOVACAO', 'TT_PRE', 'LINHA_NOVA'];
  const planoDoDependente = (linha: LinhaComposicao, tipoDep: 'incluso' | 'pago' | 'dados') =>
    planoDependenteCpf(catDoTipo(linha.tipo, linha.tipoBloco, linha.categoriaCustom) ?? '', tipoDep);
  const comPlanoDeDependente = (linha: LinhaComposicao, tipoDep: 'incluso' | 'pago' | 'dados'): LinhaComposicao => {
    const plano = planoDoDependente(linha, tipoDep);
    if (!plano) return linha;
    const valor = Number(plano.valor || 0);
    return {
      ...linha, planoId: plano.id, plano,
      ...(linha.tipo === 'RENOVACAO' ? { planoNovo: plano.nome, receitaRenovada: valor, receitaAdicionada: 0, ticketNovo: valor } : {}),
    };
  };
  // o dependente segue a ordem do plano: se um sai, o próximo vira incluído
  useEffect(() => {
    if (!familiaCpf || familiaCpf.tipoEsperado.size === 0) return;
    const trocar = linhas.filter((l) => {
      const esperado = familiaCpf.tipoEsperado.get(chaveLinha(l));
      return esperado && l.plano?.dependente !== esperado && planoDoDependente(l, esperado);
    });
    if (!trocar.length) return;
    setLinhas((prev) => prev.map((l) => {
      const esperado = familiaCpf.tipoEsperado.get(chaveLinha(l));
      return esperado && l.plano?.dependente !== esperado ? comPlanoDeDependente(l, esperado) : l;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familiaCpf]);

  const dependentesDaLinha = (titular: LinhaComposicao) =>
    linhas.filter((l) => l.titularLinhaId && l.titularLinhaId === chaveLinha(titular));
  const adicionarDependente = (titular: LinhaComposicao) => {
    const vagas = vagasDoTitular(metaDoPlano(titular.plano), dependentesDaLinha(titular).length);
    if (vagas.total <= 0) {
      toast.info('Este plano já está com todos os dependentes', { description: 'Escolha um Pós maior para colocar mais uma pessoa.' });
      return;
    }
    const tipo: TipoLinha = TIPOS_DEPENDENTE.includes(titular.tipo) ? titular.tipo : 'PORTABILIDADE';
    const base: LinhaComposicao = {
      id: `dependente-${Date.now()}`,
      linha_id: crypto.randomUUID(),
      numero: tipo === 'LINHA_NOVA' ? 'Linha Nova' : '',
      tipo,
      ...(titular.categoriaCustom && tipo === titular.tipo ? { categoriaCustom: titular.categoriaCustom } : {}),
      titularLinhaId: chaveLinha(titular),
      planoId: null,
    };
    const nova = comPlanoDeDependente(base, vagas.inclusos > 0 ? 'incluso' : 'pago');
    if (!nova.plano) {
      toast.error('O dependente desta operação não está no catálogo CPF', { description: 'Avise a gestão para conferir Planos & Preços.' });
      return;
    }
    setLinhas((prev) => {
      const ultimo = [titular, ...prev.filter((l) => l.titularLinhaId === chaveLinha(titular))].map((l) => prev.indexOf(l)).reduce((a, b) => Math.max(a, b), -1);
      return [...prev.slice(0, ultimo + 1), nova, ...prev.slice(ultimo + 1)];
    });
  };
  const tornarDependente = (linha: LinhaComposicao, titularChave: string) => {
    const titular = linhas.find((l) => chaveLinha(l) === titularChave);
    if (!titular) return;
    const vagas = vagasDoTitular(metaDoPlano(titular.plano), dependentesDaLinha(titular).length);
    if (vagas.total <= 0) {
      toast.info('Este plano já está com todos os dependentes', { description: 'Escolha um Pós maior para colocar mais uma pessoa.' });
      return;
    }
    setLinhas((prev) => prev.map((l) => (l.id === linha.id
      ? comPlanoDeDependente({ ...l, titularLinhaId: titularChave }, vagas.inclusos > 0 ? 'incluso' : 'pago')
      : l)));
  };
  const tornarTitular = (linha: LinhaComposicao) => {
    const opcoes = getPlanosPorTipo(linha.tipo, linha.categoriaCustom, linha.tipoBloco);
    setLinhas((prev) => prev.map((l) => {
      if (l.id !== linha.id) return l;
      const { titularLinhaId: _sai, ...resto } = l;
      return { ...resto, planoId: opcoes[0]?.id ?? null, plano: opcoes[0] };
    }));
  };
  // O dependente escolhe a própria operação e o tipo (Douglas 01/10/2026): "eu preciso
  // escolher se eu quero que meu dependente seja uma portabilidade ou uma linha nova",
  // e "dependente para dados ou voz + dados". Voz + dados volta à ordem do plano
  // (incluído ou pago); só dados não ocupa vaga de incluído.
  const trocarOperacaoDependente = (linha: LinhaComposicao, tipo: TipoLinha) => {
    if (tipo === linha.tipo) return;
    const dep = (linha.plano?.dependente as 'incluso' | 'pago' | 'dados' | undefined) ?? 'pago';
    const base: LinhaComposicao = {
      ...linha, tipo, categoriaCustom: undefined, tipoBloco: undefined,
      numero: tipo === 'LINHA_NOVA' ? 'Linha Nova' : '',
      ...(linha.tipo === 'RENOVACAO' ? { planoNovo: undefined, receitaRenovada: undefined, receitaAdicionada: undefined, ticketNovo: undefined } : {}),
    };
    const nova = comPlanoDeDependente(base, dep);
    if (!planoDoDependente(base, dep)) {
      toast.error('O dependente desta operação não está no catálogo CPF', { description: 'Avise a gestão para conferir Planos & Preços.' });
      return;
    }
    setLinhas((prev) => prev.map((l) => (l.id === linha.id ? nova : l)));
  };
  const trocarTipoDependente = (linha: LinhaComposicao, tipoDep: 'voz' | 'dados') => {
    const alvo = tipoDep === 'dados' ? 'dados' : 'pago';
    if ((tipoDep === 'dados') === (linha.plano?.dependente === 'dados')) return;
    if (!planoDoDependente(linha, alvo)) {
      toast.error('Este tipo de dependente não está no catálogo CPF', { description: 'Avise a gestão para conferir Planos & Preços.' });
      return;
    }
    setLinhas((prev) => prev.map((l) => (l.id === linha.id ? comPlanoDeDependente(l, alvo) : l)));
  };
  const titularesPos = linhas.filter((l) => !l.titularLinhaId && l.plano?.familiaMovel === 'POS' && TIPOS_DEPENDENTE.includes(l.tipo));
  const telefoneCurto = (l: LinhaComposicao) => {
    const d = onlyDigits(String(l.numero ?? ''));
    return d.length >= 10 ? formatPhone(d) : (l.plano?.nome ?? 'titular');
  };

  // Categorias customizadas (criadas pelo admin) presentes no catálogo, com opções ativas.
  // Cada uma vira uma caixa "Serviço" automática no montador. Vazio em produção até o admin criar.
  const categoriasCustom = useMemo(() => {
    const set = new Set<string>();
    for (const r of catalogoRows) {
      if (r.ativo && !BUILTIN_CATS.has(r.categoria)) set.add(r.categoria);
    }
    return Array.from(set).sort();
  }, [catalogoRows]);
  const labelCategoria = (cat: string) =>
    cat.replace(/_/g, ' ').replace(/\b([a-z])/g, (m) => m.toUpperCase()).replace(/\bTi\b/g, 'TI').replace(/\bTv\b/g, 'TV');

  // Nome + cor de um botão de tipo vindos do registro proposta_tipos (1:1 com Config).
  // Renomear/recolorir um tipo em Configurações ▸ Planos & Preços reflete aqui.
  const btnTipo = (cat: string, fbNome: string) => {
    const t = tiposByCategoria.get(cat);
    const cc = corClassesTipo(t?.cor ?? 'primary');
    return { nome: t?.nome ?? fbNome, border: cc.border, text: cc.text };
  };

  // Tipo REAL de uma linha de categoria custom — vem do comportamento (kind) declarado
  // em proposta_tipos (ex.: pn_incremento tem kind='portabilidade' → linha PORTABILIDADE).
  // 'SERVICO' não existe como produto: é só o fallback de renderização para kind='servico'
  // (linha sem número); a conversão resolve pelo produto_pedido_id da opção do catálogo.
  const KIND_PARA_TIPO: Record<string, TipoLinha> = {
    tt: 'TT', tt_pre: 'TT_PRE', portabilidade: 'PORTABILIDADE', renov: 'RENOVACAO',
    nova: 'LINHA_NOVA', bloco: 'NOVA', fibra: 'FIBRA', tv: 'TV',
  };
  const tipoRealDaCategoria = (cat: string): TipoLinha =>
    KIND_PARA_TIPO[tiposByCategoria.get(cat)?.kind ?? ''] ?? 'SERVICO';

  // Rótulo do badge da linha: categoria custom mostra o NOME do tipo (proposta_tipos),
  // não o label genérico do tipo builtin (ex.: "PN INCREMENTO" em vez de "PN NOVO").
  const labelTipoLinha = (linha: LinhaComposicao): string =>
    (linha.categoriaCustom && tiposByCategoria.get(linha.categoriaCustom)?.nome) || getTipoLinhaLabel(linha.tipo).label;

  // ── Trocar o PRODUTO da linha na revisão de cancelado (Douglas 03/08) ──────
  //
  // Antes só dava para mexer no plano (12GB → 40GB). Trocar o tipo obrigava a
  // criar um pedido novo — o item cancelado ficava parado e o número se perdia.
  // A RPC `api_aplicar_produto_item_revisao` já troca `produto_id` no MESMO item
  // (ela libera a trava estrutural e devolve a linha atualizada), então isso aqui
  // é só a tela que faltava.
  //
  // Fica DENTRO DA FAMÍLIA de propósito: móvel usa telefone/DDD/chip/doadora e
  // fixa usa endereço de instalação. Trocar entre famílias deixaria o item com
  // campo órfão (telefone sem endereço, ou o contrário).
  const FAMILIA_MOVEL: TipoLinha[] = ['TT', 'TT_PRE', 'PORTABILIDADE', 'LINHA_NOVA', 'RENOVACAO', 'NOVA'];
  const FAMILIA_FIXA: TipoLinha[] = ['FIBRA', 'TV'];

  /** Opções de troca para a linha: os outros tipos da mesma família. */
  const tiposDaMesmaFamilia = (linha: LinhaComposicao): TipoLinha[] => {
    if (linha.categoriaCustom) return []; // categoria custom do admin: não mexe
    if (FAMILIA_MOVEL.includes(linha.tipo)) return FAMILIA_MOVEL;
    if (FAMILIA_FIXA.includes(linha.tipo)) return FAMILIA_FIXA;
    return [];
  };

  /** Só linha de item CANCELADO troca de produto — protegida/finalizada não. */
  const podeTrocarProduto = (linha: LinhaComposicao): boolean =>
    revisaoDeCancelado && !isReadOnly && !isLinhaTravada(linha) && tiposDaMesmaFamilia(linha).length > 1;

  /**
   * Troca o tipo mantendo o MESMO item (o número do pedido não muda).
   * O plano vai para a 1ª opção do tipo novo — o valor se ajusta na hora e o
   * dropdown de plano continua ali para afinar. O número de telefone é limpo
   * quando o tipo novo não usa telefone (ex.: PN → Bloco/HA), senão sobraria um
   * número pendurado num produto que não tem linha.
   */
  const trocarTipoDaLinha = (linha: LinhaComposicao, novoTipo: TipoLinha) => {
    if (novoTipo === linha.tipo) return;
    const usaTelefone = (t: TipoLinha) => t === 'TT' || t === 'TT_PRE' || t === 'PORTABILIDADE' || t === 'RENOVACAO';
    // 'NOVA' (Bloco) precisa de sub-tipo; ao entrar nele, começa em NOVO (= HA NOVO).
    const tipoBloco = novoTipo === 'NOVA' ? (linha.tipoBloco ?? 'NOVO') : undefined;
    const opcoes = getPlanosPorTipo(novoTipo, undefined, tipoBloco);
    const telefoneAnterior = String(linha.numero ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
    // HA/LINHA_NOVA ainda precisa do DDD para a implantação, mas nunca pode
    // carregar o número da antiga portabilidade. Bloco não carrega nem DDD.
    const numeroNovo = usaTelefone(novoTipo)
      ? linha.numero
      : novoTipo === 'LINHA_NOVA'
        ? telefoneAnterior.slice(0, 2)
        : '';

    // A troca é atômica também para os acompanhantes. O id da linha dona é a
    // identidade durável; o telefone antigo deixa de ser usado como vínculo.
    setLinhas(prev => prev.map(item => {
      if (item.id === linha.id) {
        return {
          ...item,
          tipo: novoTipo,
          tipoBloco,
          planoId: opcoes[0]?.id ?? null,
          numero: numeroNovo,
        };
      }
      if (item.tipo === 'SERVICO' && servicoPertenceALinha(item, linha)) {
        return {
          ...item,
          linhaVinculadaId: linha.linha_id,
          linhaVinculada: usaTelefone(novoTipo) ? item.linhaVinculada : undefined,
        };
      }
      return item;
    }));
  };

  // Cor do card: tipo custom (ex.: Fibra Next) usa a cor configurada no Config
  // (proposta_tipos.cor), não a cor fixa do tipo real. Nativo mantém TIPO_CORES.
  const coresDaLinha = (linha: LinhaComposicao): { bg: string; text: string; border: string } =>
    linha.categoriaCustom
      ? corClassesTipo(tiposByCategoria.get(linha.categoriaCustom)?.cor ?? 'primary')
      : TIPO_CORES[linha.tipo];

  // Lista de números formatados disponíveis para TT/RENOVACAO (vindos da discadora ou fatura)
  const numerosDisponiveis = useMemo(() => {
    return linhasIniciais.map(num => formatarTelefoneLocal(num));
  }, [linhasIniciais]);

  // Números já usados em linhas TT/TT_PRE (para evitar duplicação)
  const numerosUsadosTT = useMemo(() => {
    return linhas
      .filter(l => (l.tipo === 'TT' || l.tipo === 'TT_PRE') && l.numero)
      .map(l => l.numero);
  }, [linhas]);

  // Números já usados em linhas RENOVACAO (para evitar duplicação)
  const numerosUsadosRenovacao = useMemo(() => {
    return linhas
      .filter(l => l.tipo === 'RENOVACAO' && l.numero)
      .map(l => l.numero);
  }, [linhas]);

  // Números já usados em linhas PORTABILIDADE (para evitar duplicação)
  const numerosUsadosPortabilidade = useMemo(() => {
    return linhas
      .filter(l => l.tipo === 'PORTABILIDADE' && l.numero)
      .map(l => l.numero);
  }, [linhas]);

  // ========== MODO TABELA COMPACTA ==========
  // Linhas móveis = TT, TT_PRE, PORTABILIDADE, RENOVACAO, LINHA_NOVA (não FIBRA/TV/NOVA)
  const linhasMoveisFiltradas = useMemo(() => 
    linhas.filter(l => ['TT', 'TT_PRE', 'PORTABILIDADE', 'RENOVACAO', 'LINHA_NOVA'].includes(l.tipo))
  , [linhas]);
  
  // ── SERVIÇO DIGITAL DENTRO DA LINHA (Douglas 01/09/2026) ────────────────────
  // O Passaport sempre morou dentro do card da linha; o serviço digital ficava
  // num card à parte, com o telefone redigitado à mão. Dois desenhos para a
  // mesma ideia. Agora a licença é uma tira de chips na própria linha, e o card
  // separado só existe para licença SEM linha vinculada (software que não é de
  // telefone nenhum). O modelo de dados não muda: continua linha SERVICO com
  // categoriaCustom + planoId + quantidade + linhaVinculada, e vira item próprio
  // no pedido (qtd_sd / fat_ti), como sempre.
  const opcoesServicoDigital = useMemo(() => {
    const lista: Array<{ categoria: string; plano: PlanoCNPJ }> = [];
    for (const cat of categoriasCustom) {
      if (tipoRealDaCategoria(cat) !== 'SERVICO' || CATEGORIAS_SERVICO_SOLTO.includes(cat)) continue;
      for (const plano of getPlanosPorTipo('SERVICO', cat)) lista.push({ categoria: cat, plano });
    }
    return lista;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriasCustom, tiposByCategoria, catalogoRows]);

  const mesmoNumero = (a?: string | null, b?: string | null) => {
    const da = String(a ?? '').replace(/\D/g, '');
    const db = String(b ?? '').replace(/\D/g, '');
    if (da.length < 10 || db.length < 10) return false;
    return da === db || da.endsWith(db) || db.endsWith(da);
  };
  // Vínculo do serviço com a linha: PRIMEIRO pelo linha_id (estável, existe até
  // em linha sem telefone), e só depois pelo telefone — que é o que as
  // composições salvas antes de 01/09 têm. Casar só por telefone quebrava toda
  // linha que nasce sem número (Bloco, Linha Nova, PN sem número da discadora).
  const servicoPertenceALinha = (servico: LinhaComposicao, linha: LinhaComposicao) => {
    if (servico.linhaVinculadaId && linha.linha_id) return servico.linhaVinculadaId === linha.linha_id;
    return mesmoNumero(servico.linhaVinculada, linha.numero);
  };
  const servicosDaLinha = (linha: LinhaComposicao) =>
    linhas.filter((l) => l.tipo === 'SERVICO' && l.categoriaCustom !== 'aparelhos' && servicoPertenceALinha(l, linha));
  const aparelhosLegadosDaLinha = (linha: LinhaComposicao) =>
    linhas.filter((l) => l.tipo === 'SERVICO' && l.categoriaCustom === 'aparelhos' && servicoPertenceALinha(l, linha));
  const servicoJaDesenhadoNaLinha = (servico: LinhaComposicao) =>
    servico.tipo === 'SERVICO'
    && linhas.some((m) => m.tipo !== 'SERVICO' && servicoPertenceALinha(servico, m));

  const alternarServicoNaLinha = (linha: LinhaComposicao, categoria: string, plano: PlanoCNPJ) => {
    if (isReadOnly || isLinhaTravada(linha)) return;
    inicializouRef.current = true;
    const existente = servicosDaLinha(linha).find((l) => l.planoId === plano.id);
    if (existente) {
      setLinhas((prev) => prev.filter((l) => l.id !== existente.id));
      return;
    }
    const telefoneDaLinha = String(linha.numero ?? '').replace(/\D/g, '').length >= 10
      ? linha.numero
      : undefined;
    setLinhas((prev) => [...prev, {
      id: `servico-${Date.now()}-${prev.length}`,
      linha_id: crypto.randomUUID(),
      numero: '',
      tipo: 'SERVICO' as TipoLinha,
      categoriaCustom: categoria,
      planoId: plano.id,
      plano,
      quantidade: 1,
      linhaVinculadaId: linha.linha_id,
      linhaVinculada: telefoneDaLinha,
    }]);
  };

  const ajustarLicencas = (servicoId: string, delta: number) => {
    setLinhas((prev) => prev.flatMap((l) => {
      if (l.id !== servicoId) return [l];
      const nova = Math.max(0, (l.quantidade ?? 1) + delta);
      if (nova > 1 && linhaEhServicoDigital(l)) {
        toast.info('Um de cada serviço por linha', { description: MENSAGEM_SERVICO_NA_LINHA });
        return [l];
      }
      return nova === 0 ? [] : [{ ...l, quantidade: nova }];
    }));
  };

  const linhasNaoMoveis = useMemo(() =>
    linhas.filter(l => ['FIBRA', 'TV', 'NOVA', 'SERVICO'].includes(l.tipo))
      // licença já desenhada dentro da linha não vira card solto
      .filter(l => !servicoJaDesenhadoNaLinha(l))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [linhas]);

  // Auto-ativar modo compacto se 10+ linhas móveis
  // 59a — FAIXA DA LINHA no card: limites reais + matriz das Regras de Produtividade
  const mesAtualRen = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' });
  const { data: paramsRen } = useRenovacaoParametros(temLinhaRenovacao ? mesAtualRen : '');
  const { data: matrizRen } = useQuery({
    queryKey: ['renovacao-matriz-consultor-lead'],
    staleTime: 5 * 60_000,
    enabled: temLinhaRenovacao,
    queryFn: async () => {
      const { data: rules, error } = await supabase
        .from('commission_rules')
        .select('id, faixa_faturamento, competencia')
        .eq('cargo', 'CONSULTOR')
        .eq('ativo', true)
        .is('usuario_id', null)
        .order('competencia', { ascending: false });
      if (error) throw error;
      const porFaixa = new Map<number, { id: string; faixa_faturamento: number }>();
      for (const r of (rules || []) as Array<{ id: string; faixa_faturamento: number }>) {
        if (!porFaixa.has(r.faixa_faturamento)) porFaixa.set(r.faixa_faturamento, r);
      }
      const vigentes = [...porFaixa.values()].sort((a, b) => a.faixa_faturamento - b.faixa_faturamento);
      const ids = vigentes.map((r) => r.id);
      if (ids.length === 0) return { faixasFat: [] as number[], pct: new Map<string, number>() };
      const { data: celulas, error: e2 } = await supabase
        .from('commission_rule_renovacao_delta' as never)
        .select('commission_rule_id, faixa_delta, percentual')
        .in('commission_rule_id', ids);
      if (e2) throw e2;
      const pct = new Map<string, number>();
      for (const c of (celulas || []) as Array<{ commission_rule_id: string; faixa_delta: number; percentual: number }>) {
        const rule = vigentes.find((r) => r.id === c.commission_rule_id);
        if (rule) pct.set(`${rule.faixa_faturamento}:${c.faixa_delta}`, Number(c.percentual));
      }
      return { faixasFat: vigentes.map((r) => r.faixa_faturamento), pct };
    },
  });
  const faixaDaLinhaRen = (delta: number | null) => {
    if (delta === null) return null;
    return (paramsRen?.faixas || []).filter((f: RenovacaoFaixaParametro) => Number(f.tabela) === 1).find((f) =>
      (f.delta_min === null || delta >= Number(f.delta_min))
      && (f.delta_max === null || delta <= Number(f.delta_max)),
    )?.faixa ?? null;
  };
  const [modoCompacto, setModoCompacto] = useState(() => {
    const quantidadeExterna = (composicaoExterna?.linhas || []).filter((linha) => linha.tipo !== 'FIBRA' && linha.tipo !== 'TV').length;
    return Math.max(quantidadeExterna, linhasIniciais.length, qtdLinhasIniciais) >= 10;
  });

  // Quantidade em lote por tipo de linha (para adição em massa)
  const [qtdLote, setQtdLote] = useState<Record<string, number>>({
    TT: 1, TT_PRE: 1, PORTABILIDADE: 1, LINHA_NOVA: 1, RENOVACAO: 1,
    NOVA: 1, NOVA_INCREMENTO: 1, NOVA_RENOVACAO: 1,
  });
  // ⛔ A auto-ativação da tabela ignorava a escolha do consultor: com 10+ linhas,
  // clicar em "Cards" dava um lampejo e voltava para a tabela na hora — ele
  // clicava várias vezes achando que a tela travou. Escolha manual manda.
  // (Douglas 02/09/2026)
  const modoEscolhidoNaMaoRef = useRef(false);
  useEffect(() => {
    if (modoEscolhidoNaMaoRef.current) return;
    if (linhasMoveisFiltradas.length >= 10 && !modoCompacto) {
      setModoCompacto(true);
    }
  }, [linhasMoveisFiltradas.length, modoCompacto]);

  // Seleção para ação em massa
  const [linhasSelecionadas, setLinhasSelecionadas] = useState<Set<string>>(new Set());
  const [planoMassa, setPlanoMassa] = useState<string>('pos-45');

  // Atualizar planoMassa quando seleção muda para usar plano correto do tipo
  useEffect(() => {
    if (linhasSelecionadas.size > 0) {
      const tiposSelecionados = linhasMoveisFiltradas
        .filter(l => linhasSelecionadas.has(l.id))
        .map(l => l.tipo);
      
      const todosPortabilidade = tiposSelecionados.every(t => t === 'PORTABILIDADE');
      const todosRenovacao = tiposSelecionados.every(t => t === 'RENOVACAO');
      
      // Ajustar plano default para o tipo selecionado
      if (todosPortabilidade && !planoMassa.startsWith('port-')) {
        setPlanoMassa('port-70'); // Default portabilidade
      } else if (todosRenovacao && !planoMassa.startsWith('ren-')) {
        setPlanoMassa('ren-25');
      } else if (!todosPortabilidade && planoMassa.startsWith('port-')) {
        setPlanoMassa(todosRenovacao ? 'ren-25' : 'pos-45');
      }
    }
  }, [linhasSelecionadas, linhasMoveisFiltradas, planoMassa]);

  // Função para aplicar plano em massa
  const aplicarPlanoEmMassa = () => {
    if (linhasSelecionadas.size === 0) {
      toast.warning('Selecione pelo menos uma linha');
      return;
    }
    
    setLinhas(prev => prev.map(linha => {
      if (!linhasSelecionadas.has(linha.id)) return linha;
      if (isLinhaTravada(linha)) return linha; // BACKSTOP: não aplica em massa a linha finalizada. (Douglas 16/06)
      
      // Determinar plano correto baseado no tipo da linha
      const tipoLinha = linha.tipo;
      let planoId = planoMassa;
      
      // Se for portabilidade, usar plano de portabilidade correspondente
      if (tipoLinha === 'PORTABILIDADE') {
        const gb = getPlanoById(planoMassa)?.gb ?? 0;
        const planoPort = PLANOS_PORTABILIDADE_CNPJ.find(p => p.gb === gb)
          ?? PLANOS_PORTABILIDADE_CNPJ.find(p => p.id === planoMassa);
        planoId = planoPort?.id ?? planoMassa;
      }
      
      const opcoes = getPlanosPorTipo(linha.tipo, linha.categoriaCustom, linha.tipoBloco);
      const plano = opcoes.find(p => p.id === planoId)
        ?? getPlanoById(planoId, catDoTipo(linha.tipo, linha.tipoBloco, linha.categoriaCustom));
      const receitaRenovada = linha.tipo === 'RENOVACAO' ? Number(plano?.valor || 0) : undefined;
      return {
        ...linha,
        planoId,
        plano,
        ...(linha.tipo === 'RENOVACAO' ? {
          planoNovo: plano?.nome || '',
          receitaRenovada,
          receitaAdicionada: 0,
          ticketNovo: receitaRenovada,
        } : {}),
      };
    }));
    
    toast.success(`Plano aplicado a ${linhasSelecionadas.size} linhas`);
    setLinhasSelecionadas(new Set());
  };

  // Inicializar com linhas do lead (como TT ou RENOVACAO) ou composição externa
  // CORREÇÃO 2: Criar linhas placeholder se não tiver números mas tiver qtdLinhasIniciais
  //
  // ⛔ A TELA TREMENDO AO ADICIONAR ITEM NASCIA AQUI (Douglas 01/09/2026).
  // A régua anterior comparava a composição externa com as LINHAS LOCAIS: se
  // fossem diferentes, adotava a externa. Só que ao adicionar um item as duas
  // SEMPRE ficam diferentes por um instante — o modal ainda não recebeu a
  // novidade. O efeito então repunha a composição velha, o efeito de notificação
  // logo abaixo mandava a nova, o modal devolvia, e o item entrava e saía a cada
  // ciclo: é isso que a equipe via como piscar/oscilar (e o resumo mostrando um
  // plano diferente do da linha, cada um de um lado do vai-e-vem).
  //
  // A régua certa é comparar a externa com A ÚLTIMA EXTERNA QUE ADOTAMOS. Assim
  // "o modal mandou algo novo" (hidratação, fatura reconciliada, troca de lead)
  // continua entrando, e "o modal só devolveu o eco da minha própria edição" não
  // desfaz nada. O modal segue sendo o dono da composição — quando ele normaliza
  // e devolve, a chave muda, adotamos uma vez e para.
  useEffect(() => {
    if (composicaoExterna && composicaoExterna.linhas) {
      const externaKey = chaveLinhasComposicao(composicaoExterna.linhas);
      if (externaKey === ultimaExternaRef.current) return;
      ultimaExternaRef.current = externaKey;
      if (externaKey === chaveLinhasComposicao(linhasAtuaisRef.current)) return;
      // Eco ATRASADO: o modal devolveu um estado que este montador emitiu agora
      // há pouco, mas o consultor já digitou por cima. Adotá-lo faria a linha
      // andar para trás — foi assim que o telefone parou em "51995".
      const emitidaEm = chavesEmitidasRef.current.get(externaKey);
      if (emitidaEm && Date.now() - emitidaEm < JANELA_ECO_MS) return;
      linhasAtuaisRef.current = composicaoExterna.linhas;
      setLinhas(composicaoExterna.linhas);
      inicializouRef.current = true;
    } else if (linhasIniciais.length > 0 && !inicializouRef.current) {
      // Linhas existentes com números: tipo baseado em tipoLinhaInicial
      const tipoId = tipoLinhaInicial.toLowerCase();
      // Sempre iniciar com plano default 45GB para que o comparativo já mostre valores
      // O default TEM que ser da família da linha: PORTABILIDADE com plano 'pos-45'
      // (família Pós) nascia com plano que não existe nas opções dela — o campo
      // "Plano novo" abria vazio e a linha entrava valendo R$ 0,00. Mesma régua
      // que a hidratação do modal já usava. (Douglas 01/09/2026)
      const planoDefault = tipoLinhaInicial === 'RENOVACAO' ? 'ren-25'
        : tipoLinhaInicial === 'PORTABILIDADE' ? 'port-70'
        : 'pos-45';
      const novasLinhas = linhasIniciais.map((numero, index) => {
        const numeroFormatado = formatarTelefoneLocal(numero);
        const plano = planoDefault ? getPlanoById(planoDefault, catDoTipo(tipoLinhaInicial)) : undefined;
        // Buscar dados da fatura para este número
        const dadosFatura = linhasDetalhadas?.find(l => 
          formatarTelefoneLocal(l.numero) === numeroFormatado
        );
        return {
          id: `${tipoId}-${index}`,
          linha_id: crypto.randomUUID(), // ← NOVO: Gerar linha_id para linhas iniciais
          numero: numeroFormatado,
          tipo: tipoLinhaInicial,
          planoId: planoDefault,
          plano,
          // Dados do plano atual da fatura
          gbAtual: dadosFatura?.gbAtual || 0,
          valorAtual: dadosFatura?.valorAtual || 0,
          planoAtual: dadosFatura?.planoAtual || '',
          ...(tipoLinhaInicial === 'RENOVACAO' ? {
            planoNovo: plano?.nome || '',
            receitaRenovada: Number(plano?.valor || 0),
            receitaAdicionada: 0,
            ticketNovo: Number(plano?.valor || 0),
          } : {}),
        };
      });
      setLinhas(novasLinhas);
      inicializouRef.current = true;
    } else if (linhasIniciais.length === 0 && qtdLinhasIniciais > 0 && !inicializouRef.current) {
      // SEM números, mas com quantidade esperada: criar linhas placeholder
      // Isso garante que o comparativo sempre apareça com valores reais
      const tipoId = tipoLinhaInicial.toLowerCase();
      // O default TEM que ser da família da linha: PORTABILIDADE com plano 'pos-45'
      // (família Pós) nascia com plano que não existe nas opções dela — o campo
      // "Plano novo" abria vazio e a linha entrava valendo R$ 0,00. Mesma régua
      // que a hidratação do modal já usava. (Douglas 01/09/2026)
      const planoDefault = tipoLinhaInicial === 'RENOVACAO' ? 'ren-25'
        : tipoLinhaInicial === 'PORTABILIDADE' ? 'port-70'
        : 'pos-45';
      const novasLinhas = Array.from({ length: qtdLinhasIniciais }, (_, index) => ({
        id: `${tipoId}-placeholder-${index}`,
        linha_id: crypto.randomUUID(), // ← NOVO: Gerar linha_id para placeholders
        numero: '', // Número vazio = usuário preenche depois
        tipo: tipoLinhaInicial,
        planoId: planoDefault,
        plano: getPlanoById(planoDefault, catDoTipo(tipoLinhaInicial)),
      }));
      setLinhas(novasLinhas);
      inicializouRef.current = true;
    }
  }, [composicaoExterna, linhasIniciais, linhasDetalhadas, tipoLinhaInicial, qtdLinhasIniciais]);

  // Calcular se tem fibra e TV
  const temFibra = linhas.some(l => l.tipo === 'FIBRA');
  const temTV = linhas.some(l => l.tipo === 'TV');

  // Notificar mudanças (apenas quando realmente mudar)
  // CORREÇÃO: Não depender de inicializouRef para notificar - apenas usar para evitar loops
  useEffect(() => {
    // Só bloqueia o vazio da INICIALIZAÇÃO. Depois de inicializado, apagar a
    // última linha também tem que ser notificado/salvo — senão o esvaziamento
    // nunca persiste e a hidratação repõe tudo (Douglas 29/08: "apaga e volta").
    if (linhas.length === 0 && !inicializouRef.current) return;
    
    // CORREÇÃO: Marcar como inicializado na primeira vez que tem linhas
    if (!inicializouRef.current) {
      inicializouRef.current = true;
      console.log('🔄 [Montador] Inicializado com', linhas.length, 'linhas');
    }
    
    const planoFibra = linhas.find(l => l.tipo === 'FIBRA')?.plano;
    const totalGB = calcularTotalGB(linhas, temFibra);
    const totalValor = calcularTotalValor(linhas);
    const economiaValor = valorAtual - totalValor;
    const economiaPercent = valorAtual > 0 ? (economiaValor / valorAtual) * 100 : 0;

    const composicao: ComposicaoProposta = {
      linhas,
      incluiFibra: temFibra,
      planoFibra,
      totalGB,
      totalValor,
      economiaValor,
      economiaPercent,
    };

    // Chave primitiva para evitar loop (não usa objetos plano)
    // quantidade e linhaVinculada entram na chave: sem elas, trocar a linha
    // vinculada de um serviço (que não mexe no total) não salvava a composição.
    const linhasKey = chaveLinhasComposicao(linhas);
    const composicaoKey = `${linhasKey}|${totalValor.toFixed(2)}`;
    
    if (composicaoKey !== ultimaComposicaoRef.current) {
      ultimaComposicaoRef.current = composicaoKey;
      const agora = Date.now();
      for (const [chave, quando] of chavesEmitidasRef.current) {
        if (agora - quando > JANELA_ECO_MS) chavesEmitidasRef.current.delete(chave);
      }
      chavesEmitidasRef.current.set(linhasKey, agora);
      console.log('📤 [Montador] Notificando mudança:', linhas.length, 'linhas, R$', totalValor.toFixed(2));
      onComposicaoChange?.(composicao);
      onValoresChange?.({
        linhas: linhas.filter(l => ehLinhaDeVoz(l.tipo)).length,
        gb: totalGB,
        valor: totalValor,
      });
    }
  }, [linhas, temFibra, onComposicaoChange, onValoresChange, valorAtual]);
  
  // CLEANUP EFFECT: Notificar composição final antes de desmontar (safety net)
  // Isso garante que mesmo se o componente desmontar rapidamente, o estado é persistido
  useEffect(() => {
    return () => {
      if (linhas.length > 0 && onComposicaoChange) {
        console.log('🧹 [Montador] Cleanup - notificando composição final antes de desmontar');
        const planoFibra = linhas.find(l => l.tipo === 'FIBRA')?.plano;
        const totalGB = calcularTotalGB(linhas, temFibra);
        const totalValor = calcularTotalValor(linhas);
        const economiaValor = valorAtual - totalValor;
        const economiaPercent = valorAtual > 0 ? (economiaValor / valorAtual) * 100 : 0;
        
        const composicaoFinal: ComposicaoProposta = {
          linhas,
          incluiFibra: temFibra,
          planoFibra,
          totalGB,
          totalValor,
          economiaValor,
          economiaPercent,
        };
        onComposicaoChange(composicaoFinal);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Vazio propositalmente - só executa no unmount

  function formatarTelefoneLocal(tel: string | null | undefined) {
    if (!tel) return '';
    const digits = String(tel).replace(/\D/g, '');
    // FIX #8 (25/06): formata consistente (antes so 11 digitos; o resto saia cru/cortado).
    if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    if (digits.length === 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return tel;
  }

  const adicionarLinha = (tipo: TipoLinha, quantidade: number = 1, tipoBlocoOpt?: 'NOVO' | 'INCREMENTO' | 'RENOVACAO', categoriaCustom?: string) => {
    // HOTFIX: Marcar como inicializado na primeira interação do usuário
    if (!inicializouRef.current) {
      inicializouRef.current = true;
    }
    
    const qtd = Math.min(Math.max(1, quantidade), 200); // Limite de 200 por ação

    // 1 fibra / 1 TV POR PRODUTO (categoria). Fibra e Fibra Next são produtos
    // distintos -> podem coexistir; dois do MESMO produto, não. (antes travava
    // qualquer 2ª fibra por tipo -> clicar "+ Fibra Next" com Fibra na proposta
    // não fazia nada.)
    const jaTemCat = (t: TipoLinha) =>
      linhas.some(l => l.tipo === t && (l.categoriaCustom ?? '') === (categoriaCustom ?? ''));
    if (tipo === 'FIBRA' && jaTemCat('FIBRA')) {
      toast.info('Essa fibra já está na proposta', { description: 'Cada produto de fibra entra uma vez — remova a atual para trocar.' });
      return;
    }
    if (tipo === 'TV' && jaTemCat('TV')) {
      toast.info('Essa TV já está na proposta', { description: 'Cada produto de TV entra uma vez.' });
      return;
    }

    const novasLinhas: LinhaComposicao[] = [];
    for (let i = 0; i < qtd; i++) {
      // SERVICO: linha data-driven de categoria customizada (admin). Sem número; plano = 1ª opção pronta.
      if (tipo === 'SERVICO') {
        if (!CATEGORIAS_SERVICO_SOLTO.includes(categoriaCustom ?? '')) {
          toast.info('Serviço digital entra pela linha', { description: MENSAGEM_SERVICO_NA_LINHA });
          return;
        }
        const primeira = getPlanosPorTipo('SERVICO', categoriaCustom)[0];
        novasLinhas.push({
          id: `servico-${Date.now()}-${i}`,
          linha_id: crypto.randomUUID(),
          numero: '',
          tipo: 'SERVICO',
          categoriaCustom,
          planoId: primeira?.id ?? null,
          plano: primeira,
        });
        continue;
      }
      // Para TT/TT_PRE, encontrar primeiro número disponível (permite vazio para TAB)
      let numeroInicial = '';
      if (tipo === 'TT' || tipo === 'TT_PRE') {
        const usados = [...numerosUsadosTT, ...novasLinhas.map(l => l.numero).filter(Boolean)];
        const disponivelNaoUsado = numerosDisponiveis.find(n => !usados.includes(n));
        if (i === 0 && !disponivelNaoUsado && numerosDisponiveis.length > 0) {
          toast.info(`Linha ${tipo === 'TT_PRE' ? 'TT PRE' : 'TT POS'} adicional`, { description: 'Digite o número da linha manualmente no campo.' });
        }
        numeroInicial = disponivelNaoUsado || '';
      }

      // Para RENOVACAO, também usar números disponíveis
      if (tipo === 'RENOVACAO') {
        const usados = [...numerosUsadosRenovacao, ...novasLinhas.map(l => l.numero).filter(Boolean)];
        const disponivelNaoUsado = numerosDisponiveis.find(n => !usados.includes(n));
        if (i === 0 && !disponivelNaoUsado && numerosDisponiveis.length > 0) {
          toast.info('Linha adicional', { description: 'Digite o número da linha manualmente no campo.' });
        }
        numeroInicial = disponivelNaoUsado || '';
      }

      // default de plano = 1ª opção da categoria do tipo (sempre válida no split por tipo);
      // TT escolhe por linha (fica sem plano até o consultor selecionar).
      const opcoesTipo = getPlanosPorTipo(tipo, categoriaCustom, tipoBlocoOpt);
      const primeiraOpcao = tipo === 'TT' ? undefined : opcoesTipo[0];
      const novaLinha: LinhaComposicao = {
        id: `${tipo.toLowerCase()}-${Date.now()}-${i}`,
        linha_id: crypto.randomUUID(),
        numero: tipo === 'NOVA' ? 'Bloco de Dados'
              : tipo === 'FIBRA' ? 'Fibra'
              : tipo === 'TV' ? 'TV'
              : tipo === 'RENOVACAO' ? (numeroInicial || 'Renovação')
              : tipo === 'TT' || tipo === 'TT_PRE' ? (numeroInicial || '')
              : tipo === 'LINHA_NOVA' ? 'Linha Nova'
              : '',
        tipo,
        ...(categoriaCustom && { categoriaCustom }),
        planoId: primeiraOpcao?.id ?? null,
        plano: primeiraOpcao,
        ...(tipo === 'FIBRA' && { tipoFibra: 'CNPJ' as const }),
        ...(tipo === 'NOVA' && { tipoBloco: (tipoBlocoOpt || 'NOVO') as TipoBloco }),
      };
      // Linha da conta que volta à renovação traz o atual dela, igual a digitar
      // o número — sem isso ela voltava com R$ 0 atual no simulador.
      const daConta = tipo === 'RENOVACAO' && numeroInicial
        ? linhasDetalhadas?.find(l => formatarTelefoneLocal(l.numero) === formatarTelefoneLocal(numeroInicial))
        : undefined;
      if (daConta) {
        novaLinha.gbAtual = daConta.gbAtual;
        novaLinha.valorAtual = daConta.valorAtual;
        novaLinha.planoAtual = daConta.planoAtual;
      }
      novasLinhas.push(novaLinha);
    }

    setLinhas(prev => [...prev, ...novasLinhas]);
    if (qtd > 1) {
      const labelTipo = categoriaCustom ? (tiposByCategoria.get(categoriaCustom)?.nome ?? labelCategoria(categoriaCustom))
        : tipo === 'TT_PRE' ? 'TT PRE'
        : tipo === 'TT' ? 'TT POS'
        : tipo === 'NOVA' ? `Bloco ${tipoBlocoOpt === 'INCREMENTO' ? 'Incremento' : tipoBlocoOpt === 'RENOVACAO' ? 'Renovação' : 'Novo'}`
        : tipo;
      toast.success(`${qtd} ${tipo === 'SERVICO' ? 'licenças' : 'linhas'} ${labelTipo} adicionadas`);
    }
  };



  const renderAdicionaisCpf = (linha: LinhaComposicao, lista: Array<{ id: string; nome: string; valor: number }>, rotulo: string) => (
    <div className="mt-2 pt-2 border-t">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">{rotulo}</span>
        {lista.length === 0 && <span className="text-xs text-muted-foreground italic">nenhum adicional CPF na tabela</span>}
        {lista.map((adicional) => {
          const qtd = linha.adicionais?.[adicional.id] || 0;
          return (
            <button
              key={adicional.id}
              type="button"
              title={`+${formatarMoedaPlano(adicional.valor)}/mês · Clique: +1 · Botão direito: -1`}
              onClick={() => ajustarQuantidadeAdicional(linha.id, adicional.id, 1)}
              onContextMenu={(e) => { e.preventDefault(); if (qtd > 0) ajustarQuantidadeAdicional(linha.id, adicional.id, -1); }}
              className={cn(
                "text-xs px-2 py-0.5 rounded-full border transition-colors",
                qtd > 0
                  ? "bg-primary/[0.10] border-primary/40 text-primary dark:text-foreground font-medium"
                  : "bg-muted/50 border-border text-muted-foreground hover:border-primary/40",
              )}
            >
              {adicional.nome} · {formatarMoedaPlano(adicional.valor)}{qtd > 0 && ` (${qtd})`}
            </button>
          );
        })}
      </div>
    </div>
  );

  const removerLinha = (id: string) => {
    // BACKSTOP: não remove linha finalizada NEM linha vinculada a item do pedido —
    // apagar apagaria/órfãaria o item da planilha. (Douglas 22/06: editar ou manter, nunca apagar.)
    const alvo = linhas.find(l => l.id === id);
    if (alvo && (isLinhaTravada(alvo) || isLinhaVinculada(alvo))) return;
    // HOTFIX: Marcar como inicializado na interação
    if (!inicializouRef.current) {
      inicializouRef.current = true;
    }
    // CPF: o dependente não existe sem o titular — sai junto (Douglas 29/09/2026)
    const chaveAlvo = alvo ? chaveLinha(alvo) : null;
    const saemJunto = chaveAlvo ? linhas.filter(l => l.titularLinhaId === chaveAlvo && !isLinhaTravada(l) && !isLinhaVinculada(l)) : [];
    if (saemJunto.length) toast.info(`${saemJunto.length} dependente(s) saíram junto com o titular`);
    // CORREÇÃO: Usar setState funcional
    setLinhas(prev => prev.filter(l => l.id !== id && !saemJunto.some(d => d.id === l.id)));
  };

  const atualizarLinha = (id: string, campo: Partial<LinhaComposicao>) => {
    // BACKSTOP trava por linha: não altera linha finalizada. (Douglas 16/06)
    if (linhasTravadasIds?.size && linhas.some(l => l.id === id && isLinhaTravada(l))) return;
    // HOTFIX: Marcar como inicializado na interação
    if (!inicializouRef.current) {
      inicializouRef.current = true;
    }
    // CORREÇÃO: Usar setState funcional
    setLinhas(prev => prev.map(linha => {
      if (linha.id !== id) return linha;
      
      const atualizada = { ...linha, ...campo };

      // Se mudou o plano, buscar dados do plano NA CATEGORIA da linha primeiro —
      // o mesmo código pode existir em mais de uma categoria (ex.: port-100 em
      // pn_novo E pn_incremento), cada uma com valor e produto_pedido_id próprios.
      if (campo.planoId !== undefined) {
        const opcoes = getPlanosPorTipo(atualizada.tipo, atualizada.categoriaCustom, atualizada.tipoBloco);
        atualizada.plano = campo.planoId
          ? (opcoes.find(p => p.id === campo.planoId) ?? getPlanoById(campo.planoId, catDoTipo(atualizada.tipo, atualizada.tipoBloco, atualizada.categoriaCustom)))
          : undefined;
        if (atualizada.tipo === 'RENOVACAO') {
          const receitaRenovada = Number(atualizada.plano?.valor || 0);
          atualizada.planoNovo = atualizada.plano?.nome || '';
          atualizada.receitaRenovada = receitaRenovada;
          atualizada.receitaAdicionada = 0;
          atualizada.ticketNovo = receitaRenovada;
        }
      }

      return atualizada;
    }));
  };

  const comparativoRenovacao = (linha: LinhaComposicao) => {
    const valorAtualLinha = Number(linha.valorAtual || 0);
    const receitaInformada = Number(linha.receitaRenovada || 0);
    const valorNovoLinha = receitaInformada > 0 ? receitaInformada : Number(linha.plano?.valor || 0);
    const deltas = linha.planoId && linha.plano
      ? deltasDaRenovacao(valorAtualLinha, valorNovoLinha)
      : { economico: null, literal: null };
    const delta = parametrosRenovacao?.deltaFormulaSign === 'LITERAL_REGULAMENTO'
      ? deltas.literal
      : deltas.economico;
    return {
      valorAtual: valorAtualLinha,
      valorNovo: valorNovoLinha,
      delta,
    };
  };

  // TM de cada linha vem da fidelidade dela na conta; a trava do aparelho usa o
  // TM da regra (tmDaRenovacao, espelho de fn_renovacao_tm_regra no banco).
  const linha9 = (numero?: string | null) => onlyDigits(numero || '').slice(-9);
  const fidelidadeDaLinha = (numero?: string | null) => {
    const alvo = linha9(numero);
    if (alvo.length < 9) return null;
    return linhasDetalhadas.find((l) => linha9(l.numero) === alvo)?.dataFidelidade ?? null;
  };
  const renovacoesComNumero = linhas.filter((l) => l.tipo === 'RENOVACAO' && onlyDigits(l.numero || '').length >= 10);
  const primeiraRenovacaoId = linhas.find((l) => l.tipo === 'RENOVACAO')?.id ?? null;
  const avisoTmRenovacao = (() => {
    if (ehCpf || renovacoesComNumero.length === 0) return null;
    const regra = tmDaRenovacao({
      tmCarteira: tmCarteiraRenovacao,
      parcial: linhasDetalhadas.length > renovacoesComNumero.length,
      numerosRenovados: renovacoesComNumero.map((l) => l.numero),
      linhasConta: linhasDetalhadas,
    });
    if (regra.tm == null) return null;
    const origem = regra.origem === 'fidelidade' ? 'menor TM entre as linhas renovadas' : 'TM da carteira';
    if (regra.tm >= 12 && regra.tm <= 17) {
      const temAparelho = linhas.some((l) => l.aparelho) || linhas.some((l) => l.tipo === 'SERVICO' && l.categoriaCustom === 'aparelhos');
      return temAparelho
        ? { ok: true, texto: `TM ${regra.tm} meses (${origem}): o aparelho exigido já está na proposta.` }
        : { ok: false, texto: `TM ${regra.tm} meses (${origem}): a renovação precisa de 1 aparelho no pedido. Inclua em uma das linhas pelo + Aparelho.` };
    }
    if (regra.tm < 12) {
      return { ok: false, texto: `TM ${regra.tm} meses (${origem}): renovar não paga comissão. Para mais internet, o caminho é o Bloco de dados.` };
    }
    return null;
  })();

  // Ajustar quantidade de adicional de TV (Pontos)
  const ajustarQuantidadeAdicional = (linhaId: string, adicionalId: string, delta: number) => {
    // BACKSTOP trava por linha: não altera adicionais de linha finalizada. (Douglas 16/06)
    if (linhasTravadasIds?.size && linhas.some(l => l.id === linhaId && isLinhaTravada(l))) return;
    // HOTFIX: Marcar como inicializado na interação
    if (!inicializouRef.current) {
      inicializouRef.current = true;
    }
    setLinhas(prev => prev.map(linha => {
      if (linha.id !== linhaId) return linha;
      const adicionaisAtuais = linha.adicionais || {};
      const quantidadeAtual = adicionaisAtuais[adicionalId] || 0;
      const novaQuantidade = Math.max(0, quantidadeAtual + delta); // Mínimo 0
      
      return { 
        ...linha, 
        adicionais: { 
          ...adicionaisAtuais, 
          [adicionalId]: novaQuantidade 
        } 
      };
    }));
  };

  const planoFibra = linhas.find(l => l.tipo === 'FIBRA')?.plano;
  const totalGB = calcularTotalGB(linhas, temFibra);
  // CORREÇÃO: Passar TODAS as linhas (incluindo FIBRA) - a função soma tudo internamente
  const totalValor = calcularTotalValor(linhas);
  const economiaValor = valorAtual - totalValor;
  const economiaPercent = valorAtual > 0 ? (economiaValor / valorAtual) * 100 : 0;

  const linhasComPlano = linhas.filter(l => l.plano);
  const linhasMoveis = linhas.filter(l => l.tipo !== 'FIBRA');

  return (
    <div className="space-y-4">
      {/* ✅ (A) Alerta de proposta travada */}
      {isReadOnly && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/5 border border-warning/40 dark:bg-warning/30 dark:border-warning">
          <svg className="h-4 w-4 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-warning dark:text-warning">Proposta travada</p>
            <p className="text-xs text-warning dark:text-warning">
              Para alterar produto, plano, valor ou quantidade, use "Revisar Proposta" no pedido.
            </p>
          </div>
        </div>
      )}
      
      {/* 59a — ADICIONAR ITEM, agrupado por família e tudo à vista */}
      {(() => {
        // Grade 6 × 2 no esquadro (Douglas 02/10/2026): toda célula com a mesma
        // largura, o contador do mesmo tamanho e o texto no mesmo padrão.
        const CELULA_TIPO = 'grid h-[26px] min-w-0 grid-cols-[28px_minmax(0,1fr)] overflow-hidden rounded-full border border-[#E4DDD3] bg-card transition-colors hover:border-primary/40 dark:border-[#3D2F2F]';
        const BTN_TIPO = 'flex min-w-0 items-center px-2 text-left text-[11px] font-semibold text-foreground transition-colors hover:text-primary disabled:cursor-not-allowed disabled:text-muted-foreground dark:hover:text-[#C87484]';
        const INPUT_QTD = 'h-full w-full border-0 border-r border-[#E4DDD3] bg-transparent text-center text-[11px] tabular-nums outline-none disabled:opacity-50 dark:border-[#3D2F2F] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
        const SIGLAS = new Set(['TT', 'PN', 'POS', 'PRE', 'TV', 'HA', 'SD', 'TI']);
        const rotuloBotao = (nome: string) => nome
          .replace(/SERVICO/gi, 'Serviço')
          .split(/\s+/)
          .filter(Boolean)
          .map((p) => SIGLAS.has(p.toUpperCase()) ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
          .join(' ');
        const todasRen = linhas.length > 0 && linhas.every((l) => l.tipo === 'RENOVACAO');
        const renComNumero = linhas.filter((l) => l.tipo === 'RENOVACAO' && String(l.numero ?? '').replace(/\D/g, '').length >= 10).length;
        const linhasDaConta = linhasDetalhadas.length;
        const renovacaoParcial = renComNumero > 0 && linhasDaConta > renComNumero;
        // ⛔ Componente declarado DENTRO do render nasce novo a cada render: o
        // React desmonta e remonta o <input>, e o cursor cai fora ao primeiro
        // dígito. Era a parte do "tela tremendo" que sobrou depois do conserto do
        // telefone — o campinho de quantidade não deixava digitar. Aqui ele é
        // fixo, com props; o estado do lote continua no componente pai.
        // (Douglas 02/09/2026)
        const BotaoLote = ({ qtdKey, onAdd, nome, dica, disabled }: { qtdKey?: string; onAdd: () => void; nome: string; dica: string; Icone?: React.ComponentType<{ className?: string }>; disabled?: boolean }) => (
          <div className={CELULA_TIPO}>
            {qtdKey ? (
              <CampoQuantidadeLote
                valor={qtdLote[qtdKey] ?? 1}
                onChange={(n) => setQtdLote(prev => ({ ...prev, [qtdKey]: n }))}
                className={INPUT_QTD}
                disabled={isReadOnly}
              />
            ) : (
              <span className="grid place-items-center border-r border-[#E4DDD3] text-[9.5px] text-muted-foreground dark:border-[#3D2F2F]" title="Entra uma vez por proposta">1×</span>
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={onAdd} disabled={disabled || isReadOnly} className={BTN_TIPO}>
                    <span className="truncate">+ {rotuloBotao(nome)}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">{dica}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
        return (
          <div className="space-y-2">
            {familiaCpf && familiaCpf.erros.length > 0 && (
              <div className="rounded-lg border border-[#E9C9D0] border-l-[3px] border-l-[#5B0A0D] bg-card px-3 py-2 text-[11.5px] text-[#5B0A0D] dark:border-[#332727] dark:border-l-[#C87484] dark:text-[#C87484]">
                <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">Família no CPF · o pedido não passa assim</div>
                {familiaCpf.erros.map((e) => <p key={e}>{e}</p>)}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Adicionar item · {tipoPessoa === "PF" ? "CPF" : "CNPJ"}</span>
              <span
                className="flex-shrink-0 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-bold text-foreground"
                title={ehCpf ? "Pessoa física · Claro Residencial (book 10/09/2026) — só aparecem planos CPF" : "Empresa · Claro Empresas — só aparecem planos CNPJ"}
              >
                {ehCpf ? "Planos CPF · Claro Residencial" : "Planos CNPJ · Claro Empresas"}
              </span>
              {temLinhaRenovacao && (
                faturaVinculadaRenovacao ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-success/30 bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success"
                    title="A simulação de cada linha usa os valores reais desta fatura"
                  >
                    <Receipt className="h-3 w-3" />
                    Fatura {mesCompetenciaRenovacaoLabel}
                  </span>
                ) : tipoPessoa === "PF" ? (
                  /* ⛔ NO CPF A FATURA NÃO É EXIGIDA (Douglas 17/09/2026): o
                     documento não existe nesse fluxo e quem monta a linha é o
                     consultor. Cobrar aqui era mandar buscar papel que não há. */
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
                    title="Renovação CPF não precisa de fatura — as linhas e os valores são os que você montar aqui"
                  >
                    <Receipt className="h-3 w-3" />
                    CPF — sem fatura
                  </span>
                ) : (
                  /* Espera é dívida, e dívida é bordô — âmbar não existe na
                     paleta da casa. */
                  <span
                    className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                    title="Sem isto, Criar Pedido trava pedindo a fatura — vincule pela aba Fatura do lead"
                  >
                    <Receipt className="h-3 w-3" />
                    Sem fatura vinculada
                  </span>
                )
              )}
              <span className="min-w-0 flex-1" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ModalRegrasRenovacao trigger={<Button size="sm" variant="outline" className="h-7 text-[11px]"><BookOpen className="mr-1 h-3 w-3" />Como funciona</Button>} />
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">Entender a troca de pacote por tempo de base</p></TooltipContent>
                </Tooltip>
                {clienteId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ModalFaturaVisualizacao clienteId={clienteId} razaoSocial={razaoSocial}
                        trigger={<Button size="sm" variant="outline" className="h-7 text-[11px]"><Receipt className="mr-1 h-3 w-3" />Fatura</Button>} />
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">Ver fatura detalhada</p></TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
              {/* ⛔ Era >= 5 — com 4 linhas o botao nem aparecia, e o card
                  expandido (Plano Atual/Novo, Delta, Faixa, Beneficios,
                  Passaport, Servicos, Aparelho empilhados) obrigava rolar
                  MUITO a aba. A Tabela ja' existe e mostra o essencial
                  (numero, plano atual+GB, plano novo, valor, faixa+comissao,
                  delta) numa linha so' — so' faltava poder chegar nela com
                  poucas linhas. Threshold cai pra >1: com 1 linha so' nao ha'
                  o que comparar. (Douglas 04/09/2026: "este pedido tem 4
                  linhas e tive que rolar muito a aba") */}
              {linhasMoveisFiltradas.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => { modoEscolhidoNaMaoRef.current = true; setModoCompacto(!modoCompacto); }} className="h-7 px-2">
                  {modoCompacto ? <><LayoutList className="mr-1 h-3 w-3" /><span className="text-xs">Cards</span></> : <><LayoutGrid className="mr-1 h-3 w-3" /><span className="text-xs">Tabela</span></>}
                </Button>
              )}
              <span className="flex-shrink-0 text-[10.5px] text-muted-foreground">{linhas.length} {linhas.length === 1 ? 'item' : 'itens'} na proposta{todasRen ? ' · todos REN' : ''}</span>
              {renovacaoParcial && (
                <span className="flex-shrink-0 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary dark:text-[#C87484]" title="A Claro aprova renovação parcial: só as linhas escolhidas são renovadas. A prova de TM e classificação vai na observação do pedido.">
                  Parcial · {renComNumero} de {linhasDaConta} linhas
                </span>
              )}
            </div>
            {/* ⛔ TODOS os produtos a' VISTA (Douglas 01/09/2026). O padrão 59a
                (28/08) trocou os dez botões por uma barra de famílias: para pôr
                um PN o consultor tinha que descobrir a aba NOVO antes. Produto
                escondido atrasa venda — a barra virou rótulo do grupo e todos os
                botões voltaram para a tela, no mesmo visual.

                ⛔ E o RÓTULO TEM QUE DIZER A VERDADE (Douglas 02/09/2026):
                "RENOVAÇÃO É RENOVAÇÃO... TT POS NÃO É RENOVAÇÃO". Até aqui os
                quatro rótulos eram herança das ABAS do padrão 59a, onde cada aba
                trazia uma legenda que confessava a mistura ("REN · TT · BLOCO").
                Ao virar rótulo de grupo (01/09) a legenda foi apagada e o rótulo
                passou a AFIRMAR o que era falso. O banco concorda com o Douglas:
                TT POS/TT PRE viram TT POS-PJ/TT PRE-PJ (qtd_pp), não RENOVAÇAO.

                Cada grupo agora é um <div> próprio — antes os 18 filhos ficavam
                soltos no mesmo flex-wrap, então o rótulo desgrudava dos seus
                botões na quebra de linha e a barra parecia embaralhada.

                ⚠️ `pn_incremento`, `fibra_next` e `servico_digital` continuam
                FORA de BUILTIN_CATS de propósito: `opcoesServicoDigital` (~677)
                itera `categoriasCustom`, e esvaziar essa lista APAGA a faixa de
                Serviços de dentro da linha — que 460 leads já usam. Elas são
                posicionadas pelo nome aqui embaixo, não removidas da lista. */}
            <div className="grid grid-cols-2 gap-[5px] sm:grid-cols-3 md:grid-cols-6">
              {(() => {
                // O nome do grupo saiu da frente dos botões (Douglas 02/10/2026):
                // a ordem é a mesma, e cada botão já diz o produto.
                const GrupoBotoes = ({ children }: { rotulo: string; children: React.ReactNode }) => <>{children}</>;
                // Botão de categoria vinda do banco (proposta_tipos), posicionado
                // no grupo certo em vez de cair no balde "Outros".
                const btnCustom = (cat: string) => {
                  if (!categoriasCustom.includes(cat)) return null;
                  const tNome = tiposByCategoria.get(cat)?.nome ?? labelCategoria(cat);
                  return (
                    <BotaoLote key={cat} qtdKey={cat} onAdd={() => adicionarLinha(tipoRealDaCategoria(cat), qtdLote[cat] ?? 1, undefined, cat)} nome={tNome} dica={`Adicionar ${tNome} (configurado em Planos & Preços)`} Icone={Sparkles} />
                  );
                };
                const JA_POSICIONADAS = ['pn_incremento', 'fibra_next', 'fibra_migra', 'servico_digital'];
                const sobrando = categoriasCustom.filter((c) => !JA_POSICIONADAS.includes(c));
                return (
                  <>
                    <GrupoBotoes rotulo="Renovação">
                      <BotaoLote qtdKey="RENOVACAO" onAdd={() => adicionarLinha('RENOVACAO', qtdLote['RENOVACAO'])} nome={btnTipo('renov','Renov').nome} dica={`Renovação de linha ${tipoPessoa === "PF" ? "CPF" : "CNPJ"} existente`} Icone={RefreshCw} />
                    </GrupoBotoes>

                    <GrupoBotoes rotulo="TT">
                      {(tipoPessoa !== "PF" || catalogoRows.some((p) => p.categoria === "tt_pos" as string && p.ativo)) && <BotaoLote qtdKey="TT" onAdd={() => adicionarLinha('TT', qtdLote['TT'])} nome={btnTipo('tt_pos','TT POS').nome} dica={isReadOnly ? 'Proposta travada' : 'TT Pós-Pago (CPF Claro Pós → CNPJ Claro)'} Icone={RefreshCw} />}
                      <BotaoLote qtdKey="TT_PRE" onAdd={() => adicionarLinha('TT_PRE', qtdLote['TT_PRE'])} nome={btnTipo('tt_pre','TT PRE').nome} dica={isReadOnly ? 'Proposta travada' : (tipoPessoa === 'PF' ? 'TT Pré para CPF' : 'TT Pré-Pago (CPF Claro Pré → CNPJ Claro)')} Icone={RefreshCw} />
                    </GrupoBotoes>

                    <GrupoBotoes rotulo="Portabilidade">
                      <BotaoLote qtdKey="PORTABILIDADE" onAdd={() => adicionarLinha('PORTABILIDADE', qtdLote['PORTABILIDADE'])} nome={btnTipo('pn_novo','PN NOVO').nome} dica="Portabilidade Nova (Vivo, Tim, etc. → Claro)" Icone={ArrowRightLeft} />
                      {btnCustom('pn_incremento')}
                    </GrupoBotoes>

                    <GrupoBotoes rotulo="Linha Nova">
                      <BotaoLote qtdKey="LINHA_NOVA" onAdd={() => adicionarLinha('LINHA_NOVA', qtdLote['LINHA_NOVA'])} nome={btnTipo('nova','Nova').nome} dica="Habilitação de linha nova Claro" Icone={PhoneOutgoing} />
                    </GrupoBotoes>

                    {/* ⛔ "Bloco Renov" VOLTOU como parte da renovação (Douglas
                        18/10/2026: "temos o bloco de dados de incremento que é
                        venda nova, e temos o bloco de dados renovação, ele é usado
                        para renovação"). Vira RENOVAÇAO no pedido (Migração) e só
                        entra quando a proposta já tem linha Renov — a mesma régua
                        barra o Criar pedido e a revisão (`blocoRenovSemRenovacao`).
                        Saíra em 02/09 quando gerava o item idêntico ao Bloco Incr.
                        ⚠️ 'bloco_renov' CONTINUA em BUILTIN_CATS: tirar de lá faz
                        o botão aparecer de novo, solto, pelo `categoriasCustom`. */}
                    <GrupoBotoes rotulo="Bloco">
                      <BotaoLote qtdKey="NOVA" onAdd={() => adicionarLinha('NOVA', qtdLote['NOVA'], 'NOVO')} nome={btnTipo('bloco_novo','Bloco Novo').nome} dica="Novo Bloco de Dados (HA NOVO no pedido)" Icone={Sparkles} />
                      <BotaoLote qtdKey="NOVA_INCREMENTO" onAdd={() => adicionarLinha('NOVA', qtdLote['NOVA_INCREMENTO'], 'INCREMENTO')} nome={btnTipo('bloco_incr','Bloco Incr').nome} dica="Incremento de Bloco existente (HA INCREMENTO no pedido)" Icone={Sparkles} />
                      {tipoPessoa !== 'PF' && <BotaoLote qtdKey="NOVA_RENOVACAO" onAdd={() => adicionarLinha('NOVA', qtdLote['NOVA_RENOVACAO'], 'RENOVACAO')} disabled={!temLinhaRenovacao} nome={btnTipo('bloco_renov','Bloco Renov').nome} dica={temLinhaRenovacao ? 'Bloco de Dados da renovação (RENOVAÇAO no pedido)' : 'Adicione as linhas de renovação primeiro'} Icone={Sparkles} />}
                    </GrupoBotoes>

                    <GrupoBotoes rotulo="Fibra &amp; TV">
                      <BotaoLote onAdd={() => adicionarLinha('FIBRA')} disabled={temFibra} nome={btnTipo('fibra','Fibra').nome} dica={temFibra ? 'Já possui Fibra' : 'Adicionar Internet Fibra'} Icone={Wifi} />
                      {btnCustom('fibra_next')}
                      {/* Fibra Migra (26/10/2026): quem já tem a fibra Claro e troca de
                          pacote sobe como FIBRA MIGRA CPF, não como instalação nova.
                          Só o catálogo CPF tem opção nesta categoria. */}
                      {btnCustom('fibra_migra')}
                      {(tipoPessoa !== 'PF' || PLANOS_TV.length > 0) && <BotaoLote onAdd={() => adicionarLinha('TV')} disabled={temTV} nome={btnTipo('tv','TV').nome} dica={temTV ? 'Já possui Claro TV' : 'Adicionar Claro TV Empresas'} Icone={Tv} />}
                    </GrupoBotoes>

                    <GrupoBotoes rotulo="Serviços">
                      {/* Serviço digital não tem botão solto (Douglas 26/10/2026): entra
                          pelo "+ Serviço" no cartão da linha, um de cada por linha.
                          O M2M tem (27/10/2026): é serviço único, sem telefone dono. */}
                      {sobrando.filter((cat) => CATEGORIAS_SERVICO_SOLTO.includes(cat) || tipoRealDaCategoria(cat) !== 'SERVICO').map((cat) => btnCustom(cat))}
                      <span className="col-span-2 self-center text-[11px] text-muted-foreground">Serviço digital: toque em "+ Serviço" no cartão da linha</span>
                    </GrupoBotoes>
                  </>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Lista de linhas */}
      <div className="space-y-2">
        {linhas.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
            <Phone className="w-8 h-8 mx-auto mb-2 text-danger" />
            <p className="text-sm text-muted-foreground">
              Use os botões acima para adicionar linhas
            </p>
          </div>
        ) : modoCompacto && linhasMoveisFiltradas.length > 0 ? (
          /* ========== MODO TABELA COMPACTA ========== */
          <div className="space-y-3">
            {/* Tabela de linhas móveis */}
            <div className="border rounded-lg overflow-hidden">
              {/* Barra de ação em massa */}
              <div className="flex items-center gap-3 p-2 bg-muted/50 border-b flex-wrap">
                <Checkbox 
                  checked={linhasSelecionadas.size === linhasMoveisFiltradas.length && linhasMoveisFiltradas.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setLinhasSelecionadas(new Set(linhasMoveisFiltradas.map(l => l.id)));
                    } else {
                      setLinhasSelecionadas(new Set());
                    }
                  }}
                  disabled={isReadOnly}
                />
                <span className="text-xs text-muted-foreground">
                  {linhasSelecionadas.size > 0 
                    ? `${linhasSelecionadas.size} selecionadas` 
                    : 'Todas'}
                </span>
                
                {linhasSelecionadas.size > 0 && !isReadOnly && (
                  <>
                    <div className="h-4 border-l" />
                    <Select value={planoMassa} onValueChange={setPlanoMassa}>
                      <SelectTrigger className="w-32 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Determinar tipo predominante das linhas selecionadas
                          const tiposSelecionados = linhasMoveisFiltradas
                            .filter(l => linhasSelecionadas.has(l.id))
                            .map(l => l.tipo);
                          
                          // Usar planos específicos se todas são do mesmo tipo
                          const todosPortabilidade = tiposSelecionados.every(t => t === 'PORTABILIDADE');
                          const todosRenovacao = tiposSelecionados.every(t => t === 'RENOVACAO');
                          
                          const planos = todosPortabilidade 
                            ? PLANOS_PORTABILIDADE_CNPJ 
                            : todosRenovacao 
                              ? [...PLANOS_RENOVACAO_CNPJ, ...PLANOS_MOVEL_CNPJ]
                              : PLANOS_MOVEL_CNPJ;
                          
                          return planos.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.gb > 0 ? `${p.gb}GB` : '0GB (Voz)'} - {formatarMoedaPlano(p.valor)}{p.maxLinhasBan ? ` · máx ${p.maxLinhasBan} lin/BAN` : ''}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      size="sm" 
                      className="h-7 text-xs"
                      onClick={aplicarPlanoEmMassa}
                    >
                      Aplicar
                    </Button>
                  </>
                )}
                {(() => {
                  const marcadas = linhasMoveisFiltradas.filter((l) => linhasSelecionadas.has(l.id) && itemQueFicaDaLinha(l));
                  if (marcadas.length === 0) return null;
                  const aplicar = (fica: boolean) => {
                    marcarFicaCancelada(marcadas, fica);
                    setLinhasSelecionadas(new Set());
                  };
                  return (
                    <>
                      <div className="h-4 border-l" />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 border-[#D9B8BF] text-xs text-[#7A2235] hover:text-[#7A2235] dark:border-[#5E2229] dark:text-[#C87484]"
                        title="O cliente não quer estas linhas: elas ficam no cancelado e o resto volta ao pedido"
                        onClick={() => aplicar(true)}
                      >
                        <Ban className="h-3.5 w-3.5" />
                        Deixar canceladas
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => aplicar(false)}>
                        <RotateCcw className="h-3.5 w-3.5" />
                        Voltam
                      </Button>
                    </>
                  );
                })()}
              </div>
              
              {/* Tabela */}
              <div className={modoAmplo ? 'h-[min(46vh,430px)] min-h-[330px] overflow-auto' : 'max-h-[400px] overflow-auto'}>
                <Table noWrapper>
                  <TableHeader className="sticky top-0 bg-muted/90 backdrop-blur-sm z-10">
                    <TableRow>
                      <TableHead className="w-8 p-1.5" />
                      <TableHead className="w-20 p-1.5 text-xs">Tipo</TableHead>
                      <TableHead className="w-32 p-1.5 text-xs">Telefone</TableHead>
                      <TableHead className="w-40 p-1.5 text-xs">Plano atual</TableHead>
                      <TableHead className="w-20 p-1.5 text-xs text-right">R$ Atual</TableHead>
                      <TableHead className="w-40 p-1.5 text-xs">Plano novo</TableHead>
                      <TableHead className="w-20 p-1.5 text-xs text-right">R$ Novo</TableHead>
                      <TableHead className="w-24 p-1.5 text-xs text-right">F · Comissão</TableHead>
                      <TableHead className="w-20 p-1.5 text-xs text-right">Delta</TableHead>
                      <TableHead className="w-8 p-1.5" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linhasMoveisFiltradas.map(linha => {
                      const planosDisponiveis = getPlanosPorTipo(linha.tipo, linha.categoriaCustom, linha.tipoBloco);
                      const valorNovo = linha.plano?.valor || 0;
                      const travada = isLinhaTravada(linha) || ficaCancelada(linha); // (Douglas 16/06; fica cancelada 23/10/2026)
                      const comparativo = linha.tipo === 'RENOVACAO' ? comparativoRenovacao(linha) : null;
                      
                      const fica = ficaCancelada(linha);
                      return (
                        <TableRow
                          key={linha.id}
                          className={fica
                            ? 'bg-[#F3EFEA] hover:bg-[#F3EFEA] dark:bg-white/[0.03] [&_input]:line-through [&_button[role=combobox]]:line-through [&_.text-money-highlight]:text-muted-foreground [&_.text-money-highlight]:line-through'
                            : 'hover:bg-muted/50'}
                        >
                          {/* Checkbox */}
                          <TableCell className="p-1.5">
                            <Checkbox
                              checked={linhasSelecionadas.has(linha.id)}
                              onCheckedChange={(checked) => {
                                const novas = new Set(linhasSelecionadas);
                                if (checked) {
                                  novas.add(linha.id);
                                } else {
                                  novas.delete(linha.id);
                                }
                                setLinhasSelecionadas(novas);
                              }}
                              disabled={isReadOnly || isLinhaTravada(linha)}
                            />
                          </TableCell>
                          
                          {/* Tipo — chip NEUTRO com dot (azul não existe na paleta) */}
                          <TableCell className="p-1.5">
                            <Badge
                              variant="outline"
                              className={`shrink-0 gap-1.5 rounded-full ${CHIP_TIPO_NEUTRO}`}
                            >
                              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotDaLinha(linha.tipo)}`} />
                              {labelTipoLinha(linha)}
                            </Badge>
                            <div className="mt-0.5">{renderVinculoBadge(linha)}</div>
                          </TableCell>
                          
                          {/* Telefone */}
                          <TableCell className="p-1.5">
                            <InputTelefoneLinha
                              valor={linha.numero === 'TT' || linha.numero === 'Renovação' || linha.numero === 'Linha Nova' ? '' : linha.numero}
                              onCommit={(novoNumero) => {
                                // Buscar dados da fatura para este número
                                const dadosFatura = linhasDetalhadas?.find(l => 
                                  formatarTelefoneLocal(l.numero) === formatarTelefoneLocal(novoNumero)
                                );
                                // ⛔ Sem fatura para o número novo, o patch mandava
                                // `undefined` nos três campos e APAGAVA plano atual,
                                // R$ atual e GB da linha — corrigir um dígito zerava
                                // a coluna. Só escreve o que existe. (02/09/2026)
                                atualizarLinha(linha.id, dadosFatura
                                  ? {
                                      numero: novoNumero,
                                      gbAtual: dadosFatura.gbAtual,
                                      valorAtual: dadosFatura.valorAtual,
                                      planoAtual: dadosFatura.planoAtual,
                                    }
                                  : { numero: novoNumero });
                              }}
                              className="h-7 text-xs font-mono"
                              disabled={isReadOnly || travada}
                            />
                          </TableCell>
                          
                          {/* Plano Atual */}
                          <TableCell className="p-1.5 max-w-40">
                            <span className="block truncate text-xs text-foreground" title={linha.planoAtual || ''}>
                              {linha.tipo === 'RENOVACAO' || linha.tipo === 'PORTABILIDADE' ? (linha.planoAtual || 'Não informado') : '-'}
                            </span>
                            {(linha.tipo === 'RENOVACAO' || linha.tipo === 'PORTABILIDADE') && linha.gbAtual ? (
                              <span className="text-[10px] text-muted-foreground tabular-nums">{linha.gbAtual} GB</span>
                            ) : null}
                          </TableCell>
                          
                          {/* Valor Atual */}
                          <TableCell className="p-1.5 text-right">
                            <span className="text-xs text-muted-foreground">
                              {(linha.tipo === 'RENOVACAO' || linha.tipo === 'PORTABILIDADE') && linha.valorAtual ? formatarMoedaPlano(linha.valorAtual) : '-'}
                            </span>
                          </TableCell>
                          
                          {/* Plano Novo */}
                          <TableCell className="p-1.5">
                            <Select
                              value={linha.planoId || ''}
                              onValueChange={(value) => atualizarLinha(linha.id, { planoId: value })}
                              disabled={isReadOnly || travada}
                            >
                              <SelectTrigger className="h-[30px] rounded-lg border-[#E4DDD3] text-xs dark:border-border">
                                <SelectValue placeholder="Plano" />
                              </SelectTrigger>
                              <SelectContent>
                                {planosDisponiveis.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {ehCpf || linha.tipo === 'RENOVACAO' ? p.nome : linha.categoriaCustom ? p.nome : p.gb === 0 ? '0GB (Voz)' : `${p.gb}GB`} - {formatarMoedaPlano(p.valor)}{promoPlanoCpf(p)}{pracaPlanoCpf(p)}{p.maxLinhasBan ? ` · máx ${p.maxLinhasBan} lin/BAN` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          
                          {/* Valor Novo — money 700 */}
                          <TableCell className="p-1.5 text-right">
                            <span className="font-display text-xs font-bold tabular-nums text-money-highlight">
                              {linha.plano ? formatarMoedaPlano(comparativo?.valorNovo ?? valorNovo) : '—'}
                            </span>
                          </TableCell>

                          {/* F · Comissão POR LINHA (lei do CLAUDE.md): faixa do delta ×
                              percentual da matriz de renovação. Estimativa pela menor
                              faixa de faturamento vigente — a real é a do fechamento. */}
                          <TableCell className="p-1.5 text-right">
                            {(() => {
                              if (linha.tipo !== 'RENOVACAO' || comparativo?.delta === null || comparativo?.delta === undefined) {
                                return <span className="text-xs text-muted-foreground/40">—</span>;
                              }
                              const faixa = faixaDaLinhaRen(comparativo.delta);
                              const faixaFatBase = (matrizRen?.faixasFat || [])[0];
                              const pctLinha = faixa !== null && faixaFatBase !== undefined
                                ? matrizRen?.pct.get(`${faixaFatBase}:${faixa}`)
                                : undefined;
                              if (faixa === null || pctLinha === undefined) {
                                return <span className="text-xs text-muted-foreground/40">—</span>;
                              }
                              const comissaoLinha = (comparativo.valorNovo ?? valorNovo) * (pctLinha / 100);
                              return (
                                <span
                                  className="whitespace-nowrap text-[10.5px] tabular-nums text-muted-foreground"
                                  title={`Faixa F${faixa} × ${pctLinha}% — estimativa pela faixa de faturamento R$ ${Number(faixaFatBase).toLocaleString('pt-BR')}; a real é a do fechamento do mês`}
                                >
                                  F{faixa} × {pctLinha}% ·{' '}
                                  <span className="font-bold text-money-highlight">{formatarMoedaPlano(comissaoLinha)}</span>
                                </span>
                              );
                            })()}
                          </TableCell>

                          {/* Delta — aumento de receita é DESFECHO BOM: verde quando
                              positivo, muted no zero, bordô claro só quando negativo. */}
                          <TableCell className="p-1.5 text-right">
                            {(() => {
                              const delta = comparativo?.delta;
                              if (delta === null || delta === undefined) {
                                return <span className="text-xs text-muted-foreground/40">—</span>;
                              }
                              const cor = delta > 0
                                ? 'text-[#075E54] dark:text-[#25D366]'
                                : delta < 0
                                  ? 'text-[#7A2235] dark:text-[#C87484]'
                                  : 'text-muted-foreground';
                              return (
                                <span className={`text-xs font-semibold tabular-nums ${cor}`}>
                                  {`${delta >= 0 ? '+' : ''}${delta.toFixed(2).replace('.', ',')}%`}
                                </span>
                              );
                            })()}
                          </TableCell>

                          {/* Remover — ghost 22×22; na linha que fica cancelada, o Volta. */}
                          <TableCell className="p-1.5">
                            {fica ? renderAcaoFicaCancelada(linha, true) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-[22px] w-[22px] rounded-[7px] border border-[#E4DDD3] bg-[#FAF8F5] text-muted-foreground transition-colors hover:border-primary/50 hover:!text-primary dark:border-border dark:bg-white/[0.04] [&_svg]:h-[11px] [&_svg]:w-[11px]"
                              title="Remover esta linha da proposta"
                              onClick={() => removerLinha(linha.id)}
                              disabled={isReadOnly || travada || isLinhaVinculada(linha)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {/* Linhas não-móveis (FIBRA, TV, NOVA) continuam como cards */}
            {linhasNaoMoveis.map((linha) => {
              const cores = coresDaLinha(linha);
              const info = getTipoLinhaLabel(linha.tipo);
              const planosDisponiveis = getPlanosPorTipo(linha.tipo, linha.categoriaCustom, linha.tipoBloco);
              const travada = isLinhaTravada(linha) || ficaCancelada(linha); // (Douglas 16/06; fica cancelada 23/10/2026)

              return (
                <Card
                  key={linha.id}
                  className={cn('relative overflow-hidden rounded-r-lg rounded-l-none border border-border bg-card', ficaCancelada(linha) && 'bg-[#F3EFEA] dark:bg-white/[0.03] [&_.text-money-highlight]:text-muted-foreground [&_.text-money-highlight]:line-through')}
                >
                  <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${fileteDaLinha(linha.tipo)}`} />
                  <CardContent className="p-3 pl-4">
                    <div className="flex items-center gap-3">
                      {/* Tipo badge */}
                      <Badge
                        variant="outline"
                        className={`shrink-0 gap-1.5 rounded-full ${CHIP_TIPO_NEUTRO}`}
                      >
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotDaLinha(linha.tipo)}`} />
                        {labelTipoLinha(linha)}
                      </Badge>
                      {renderVinculoBadge(linha)}

                      {/* Nome */}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        {linha.tipo === 'FIBRA' ? (
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-sm font-medium text-foreground">
                              {linha.numero}
                            </span>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <Button
                                type="button"
                                size="sm"
                                variant={linha.tipoFibra !== 'TT' ? 'default' : 'outline'}
                                className={`h-6 px-2 text-[10px] ${
                                  linha.tipoFibra !== 'TT' 
                                    ? 'bg-info hover:bg-info text-white' 
                                    : 'border-info text-info hover:bg-info/5'
                                }`}
                                onClick={() => atualizarLinha(linha.id, { tipoFibra: 'CNPJ' })}
                                disabled={isReadOnly || travada}
                              >
                                {tipoPessoa === "PF" ? "CPF" : "CNPJ"}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant={linha.tipoFibra === 'TT' ? 'default' : 'outline'}
                                className={`h-6 px-2 text-[10px] ${
                                  linha.tipoFibra === 'TT' 
                                    ? 'bg-info hover:bg-info text-white' 
                                    : 'border-info text-info hover:bg-info/5'
                                }`}
                                onClick={() => atualizarLinha(linha.id, { tipoFibra: 'TT' })}
                                disabled={isReadOnly || travada || tipoPessoa === 'PF'}
                              >
                                TT
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <span className={`text-sm font-medium ${cores.text}`}>
                            {linha.numero}
                          </span>
                        )}
                      </div>

                      {/* Licenças — mesma régua da visão de cards. O Montador tem
                          três renderizações da mesma linha; mexer em uma só deixa
                          a outra mentindo (ver memória das 3 renderizações). */}
                      {linha.tipo === 'SERVICO' && (
                        <div className="flex items-center gap-1 shrink-0" title={travada ? motivoTrava(linha) : undefined}>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{linha.categoriaCustom === CATEGORIA_M2M ? 'Linhas' : 'Lic.'}</span>
                          <Input
                            type="number"
                            min={1}
                            value={linha.quantidade ?? 1}
                            onChange={(e) => {
                              const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                              if (n > 1 && linhaEhServicoDigital(linha)) {
                                toast.info('Um de cada serviço por linha', { description: MENSAGEM_SERVICO_NA_LINHA });
                                return;
                              }
                              atualizarLinha(linha.id, { quantidade: n });
                            }}
                            className="h-8 w-16 text-xs text-center tabular-nums"
                            disabled={isReadOnly || travada}
                          />
                        </div>
                      )}

                      {/* Seletor de plano */}
                      <div className={ehCpf ? "w-[22rem] max-w-full" : "w-44"}>
                        <Select
                          value={planosDisponiveis.some((p) => p.id === linha.planoId) ? (linha.planoId || '') : ''}
                          onValueChange={(value) => atualizarLinha(linha.id, { planoId: value })}
                          disabled={isReadOnly || travada}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder={linha.planoId && !planosDisponiveis.some((p) => p.id === linha.planoId) ? `Plano fora da tabela ${ehCpf ? "CPF" : "CNPJ"} — escolha outro` : linha.tipo === 'FIBRA' ? "Velocidade" : linha.tipo === 'TV' ? "Pacote TV" : "Plano"} />
                          </SelectTrigger>
                          <SelectContent>
                            {planosDisponiveis.map((plano) => (
                              <SelectItem key={plano.id} value={plano.id}>
                                <div className="flex items-center justify-between gap-2">
                                  <span>{ehCpf || linha.tipo === 'SERVICO' || linha.categoriaCustom ? plano.nome :linha.tipo === 'FIBRA' ? (plano.velocidade ?? plano.nome) : linha.tipo === 'TV' ? plano.nome.replace('Claro TV', '').trim() : plano.gb === 0 ? '0GB (Voz)' : `${plano.gb}GB`}</span>
                                  <span className="text-muted-foreground">
                                    {formatarMoedaPlano(plano.valor)}{promoPlanoCpf(plano)}{pracaPlanoCpf(plano)}{plano.maxLinhasBan ? ` · máx ${plano.maxLinhasBan} lin/BAN` : ''}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Valor — serviço digital multiplica pelas licenças */}
                      {linha.plano && (
                        <div className="text-right shrink-0">
                          <span className="text-sm font-semibold tabular-nums">
                            {formatarMoedaPlano(
                              linha.plano.valor * (linha.tipo === 'SERVICO' ? Math.max(1, linha.quantidade ?? 1) : 1) +
                              (linha.adicionais ? Object.entries(linha.adicionais).reduce((sum, [adicId, qtd]) => {
                                const adicTV = ADICIONAIS_TV.find(a => a.id === adicId);
                                const adicFibra = ADICIONAIS_FIBRA.find(a => a.id === adicId);
                                return sum + ((adicTV?.valor || adicFibra?.valor || 0) * (qtd || 0));
                              }, 0) : 0)
                            )}
                          </span>
                          {linha.tipo === 'SERVICO' && (linha.quantidade ?? 1) > 1 && (
                            <div className="text-[10px] text-muted-foreground tabular-nums">
                              {linha.quantidade}× {formatarMoedaPlano(linha.plano.valor)}
                            </div>
                          )}
                        </div>
                      )}

                      {renderAcaoFicaCancelada(linha, false)}
                      {/* Remover */}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removerLinha(linha.id)}
                        disabled={isReadOnly || travada || isLinhaVinculada(linha)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>

                    {/* Adicionais de FIBRA */}
                    {linha.tipo === 'FIBRA' && linha.planoId && ehCpf && renderAdicionaisCpf(linha, ADICIONAIS_FIBRA, 'Adicionais:')}
                    {linha.tipo === 'FIBRA' && linha.planoId && !ehCpf && (() => {
                      const qtdClaroFixo = linha.adicionais?.['fibra-claro-fixo'] || 0;
                      const hasMesh = (linha.adicionais?.['fibra-mesh-2'] || 0) > 0;
                      const qtdMeshAdicional = linha.adicionais?.['fibra-mesh-adicional'] || 0;
                      
                      return (
                        <div className="mt-2 pt-2 border-t">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Adicionais:</span>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => ajustarQuantidadeAdicional(linha.id, 'fibra-claro-fixo', 1)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (qtdClaroFixo > 0) {
                                        ajustarQuantidadeAdicional(linha.id, 'fibra-claro-fixo', -1);
                                      }
                                    }}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                      qtdClaroFixo > 0 
                                        ? "bg-danger/10 border-danger text-danger font-medium" 
                                        : "bg-muted/50 border-border text-muted-foreground hover:border-danger"
                                    )}
                                  >
                                    Claro Fixo {qtdClaroFixo > 0 && `(${qtdClaroFixo})`}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>+R$ 10,00/linha | Clique: +1 | Direito: -1</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-2', hasMesh ? -1 : 1)}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                      hasMesh 
                                        ? "bg-info/10 border-info text-info font-medium" 
                                        : "bg-muted/50 border-border text-muted-foreground hover:border-info"
                                    )}
                                  >
                                    Mesh 2x
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>+R$ 30,00/mês</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            {hasMesh && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (qtdMeshAdicional < 2) {
                                          ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-adicional', 1);
                                        }
                                      }}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (qtdMeshAdicional > 0) {
                                          ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-adicional', -1);
                                        }
                                      }}
                                      className={cn(
                                        "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                        qtdMeshAdicional > 0 
                                          ? "bg-info/10 border-info text-info font-medium" 
                                          : "bg-muted/50 border-border text-muted-foreground hover:border-info"
                                      )}
                                    >
                                      +Mesh {qtdMeshAdicional > 0 && `(${qtdMeshAdicional})`}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>+R$ 15,00/cada - máx 2</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Adicionais de TV */}
                    {linha.tipo === 'TV' && linha.planoId && ehCpf && renderAdicionaisCpf(linha, ADICIONAIS_TV, 'Adicionais da TV:')}
                    {linha.tipo === 'TV' && linha.planoId && !ehCpf && (() => {
                      const qtdPonto = linha.adicionais?.['tv-ponto'] || 0;
                      const qtdPonto4k = linha.adicionais?.['tv-ponto-4k'] || 0;
                      
                      return (
                        <div className="mt-2 pt-2 border-t">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">📺 Pontos:</span>
                            
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => ajustarQuantidadeAdicional(linha.id, 'tv-ponto', 1)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (qtdPonto > 0) {
                                        ajustarQuantidadeAdicional(linha.id, 'tv-ponto', -1);
                                      }
                                    }}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                      qtdPonto > 0 
                                        ? "bg-primary/[0.10] border-primary/40 text-primary dark:text-foreground font-medium" 
                                        : "bg-muted/50 border-border text-muted-foreground hover:border-primary/40"
                                    )}
                                  >
                                    Adicional {qtdPonto > 0 && `(${qtdPonto})`}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>+R$ 29,90/cada | Clique: +1 | Direito: -1</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            {linha.planoId === 'tv-4k' && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={() => ajustarQuantidadeAdicional(linha.id, 'tv-ponto-4k', 1)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (qtdPonto4k > 0) {
                                          ajustarQuantidadeAdicional(linha.id, 'tv-ponto-4k', -1);
                                        }
                                      }}
                                      className={cn(
                                        "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                        qtdPonto4k > 0 
                                          ? "bg-warning/10 border-warning text-warning font-medium" 
                                          : "bg-muted/50 border-border text-muted-foreground hover:border-warning"
                                      )}
                                    >
                                      4K {qtdPonto4k > 0 && `(${qtdPonto4k})`}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>+R$ 39,90/cada | Clique: +1 | Direito: -1</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* ========== MODO CARDS (ORIGINAL) ========== */
          /* mesma régua do modo tabela: licença já desenhada como chip dentro da
             linha não ganha card próprio — senão o consultor vê o serviço duas
             vezes e acha que entrou em dobro. (Douglas 01/09/2026) */
          linhas.filter((l) => !servicoJaDesenhadoNaLinha(l)).map((linha) => {
            const cores = coresDaLinha(linha);
            const info = getTipoLinhaLabel(linha.tipo);
            const isFibra = linha.tipo === 'FIBRA';
            const isTT = linha.tipo === 'TT' || linha.tipo === 'TT_PRE';
            const travada = isLinhaTravada(linha) || ficaCancelada(linha); // (Douglas 16/06; fica cancelada 23/10/2026)
            const planosDisponiveis = linha.titularLinhaId && linha.plano
              ? [linha.plano]
              : getPlanosPorTipo(linha.tipo, linha.categoriaCustom, linha.tipoBloco);
            const comparativo = linha.tipo === 'RENOVACAO' ? comparativoRenovacao(linha) : null;
            const numerosParaTT = numerosDisponiveis.filter(
              n => n === linha.numero || !numerosUsadosTT.includes(n)
            );

            return (
              <Card
                key={linha.id}
                className={cn('relative overflow-hidden rounded-r-lg rounded-l-none border border-border bg-card', ficaCancelada(linha) && 'bg-[#F3EFEA] dark:bg-white/[0.03] [&_.text-money-highlight]:text-muted-foreground [&_.text-money-highlight]:line-through')}
              >
                <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${fileteDaLinha(linha.tipo)}`} />
                <CardContent className="p-3 pl-4">
                  <div className="flex items-center gap-3">
                    {/* Tipo: na revisão de CANCELADO vira seletor — dá para corrigir o
                        produto (ex.: TT PRE → TT POS, PN → HA) no MESMO item, sem perder
                        o número do pedido. Fora daí continua sendo só um selo. (Douglas 03/08) */}
                    {podeTrocarProduto(linha) ? (
                      <Select
                        value={linha.tipo}
                        onValueChange={(v) => trocarTipoDaLinha(linha, v as TipoLinha)}
                      >
                        <SelectTrigger
                          className={`h-7 w-[124px] shrink-0 px-2 ${CHIP_TIPO_NEUTRO}`}
                          title="Trocar o produto deste item — o número do pedido não muda"
                        >
                          <span className="flex items-center gap-1.5 truncate">
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotDaLinha(linha.tipo)}`} />
                            {labelTipoLinha(linha)}
                          </span>
                        </SelectTrigger>
                        <SelectContentMaxPriority>
                          {tiposDaMesmaFamilia(linha).map((t) => (
                            <SelectItem key={t} value={t} className="text-xs">
                              {getTipoLinhaLabel(t).label}
                            </SelectItem>
                          ))}
                        </SelectContentMaxPriority>
                      </Select>
                    ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className={`shrink-0 gap-1.5 rounded-full ${CHIP_TIPO_NEUTRO}`}
                          >
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotDaLinha(linha.tipo)}`} />
                            {labelTipoLinha(linha)}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">{info.descricao}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    )}
                    {renderVinculoBadge(linha)}

                    {/* Número/Nome */}
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      {linha.tipo === 'SERVICO' ? (
                        /* Serviço digital fica no MESMO formato dos outros cards: badge,
                           campo de linha, plano, valor. Antes o campo ficava vazio e o
                           select de plano flutuava sozinho à direita — era o card fora do
                           padrão que o Douglas apontou em 05/08. A linha vinculada e' opcional:
                           licença de software pode nao estar amarrada a telefone nenhum. */
                        <div className="flex items-center gap-1.5 flex-1" title={travada ? motivoTrava(linha) : undefined}>
                          {/* Mesmo caminho do telefone da linha: escrever na composição
                              a cada tecla fazia a tela piscar e o número voltar sozinho.
                              (Douglas 02/09/2026) */}
                          <InputTelefoneLinha
                            valor={linha.linhaVinculada ?? ''}
                            onCommit={(digitado) => {
                              const apenasDigitos = onlyDigits(digitado);
                              if (apenasDigitos.length >= 10) {
                                atualizarLinha(linha.id, {
                                  linhaVinculada: apenasDigitos.startsWith('55') && apenasDigitos.length >= 12
                                    ? apenasDigitos.substring(2)
                                    : apenasDigitos,
                                });
                                return;
                              }
                              atualizarLinha(linha.id, {
                                linhaVinculada: (digitado.includes('(') || digitado.includes('-')) ? apenasDigitos : digitado,
                              });
                            }}
                            placeholder={linhaEhServicoDigital(linha) ? 'Linha do serviço (obrigatória)' : 'Linha vinculada (opcional)'}
                            className="h-8 text-xs font-mono flex-1"
                            disabled={isReadOnly || travada}
                          />
                          {linhaEhServicoDigital(linha) && (
                            <Select
                              value=""
                              onValueChange={(linhaId) => {
                                const dona = linhasMoveisFiltradas.find((m) => m.linha_id === linhaId);
                                if (!dona) return;
                                if (servicosDaLinha(dona).some((sv) => sv.id !== linha.id && sv.planoId === linha.planoId)) {
                                  toast.info('Esta linha já tem este serviço', { description: 'Um de cada serviço por linha.' });
                                  return;
                                }
                                const tel = String(dona.numero ?? '').replace(/\D/g, '').length >= 10 ? dona.numero : undefined;
                                atualizarLinha(linha.id, { linhaVinculadaId: dona.linha_id, linhaVinculada: tel, quantidade: 1 });
                              }}
                              disabled={isReadOnly || travada || linhasMoveisFiltradas.length === 0}
                            >
                              <SelectTrigger className="h-8 w-auto gap-1 border-[#D9B8BF] px-2 text-[11px] font-semibold text-[#7A2235] dark:border-[#5E2229] dark:text-[#C87484]" title={MENSAGEM_SERVICO_NA_LINHA}>
                                Levar para a linha
                              </SelectTrigger>
                              <SelectContent>
                                {linhasMoveisFiltradas.filter((m) => m.linha_id).map((m) => (
                                  <SelectItem key={m.linha_id} value={m.linha_id as string}>
                                    <span className="font-mono text-xs">{m.numero || getTipoLinhaLabel(m.tipo).label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {numerosDisponiveis.length > 0 && !linhaEhServicoDigital(linha) && (
                            <Select
                              value=""
                              onValueChange={(value) => atualizarLinha(linha.id, { linhaVinculada: value })}
                              disabled={isReadOnly || travada}
                            >
                              <SelectTrigger className="h-8 w-8 p-0 border-0 bg-transparent" title="Escolher uma linha do cliente">
                                <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                              </SelectTrigger>
                              <SelectContent>
                                {numerosDisponiveis.map((num) => (
                                  <SelectItem key={num} value={num}>
                                    <span className="font-mono text-xs">{num}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ) : linha.tipo === 'NOVA' || linha.tipo === 'TV' ? (
                        <span className="text-sm font-medium text-foreground">
                          {linha.numero}
                        </span>
                      ) : linha.tipo === 'FIBRA' ? (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm font-medium text-foreground">
                            {linha.numero}
                          </span>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              variant={linha.tipoFibra !== 'TT' ? 'default' : 'outline'}
                              className={`h-6 px-2 text-[10px] ${
                                linha.tipoFibra !== 'TT'
                                  ? 'bg-primary text-white hover:bg-primary'
                                  : 'border-border text-muted-foreground hover:text-primary'
                              }`}
                              onClick={() => atualizarLinha(linha.id, { tipoFibra: 'CNPJ' })}
                              disabled={isReadOnly}
                            >
                              {tipoPessoa === "PF" ? "CPF" : "CNPJ"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={linha.tipoFibra === 'TT' ? 'default' : 'outline'}
                              className={`h-6 px-2 text-[10px] ${
                                linha.tipoFibra === 'TT'
                                  ? 'bg-primary text-white hover:bg-primary'
                                  : 'border-border text-muted-foreground hover:text-primary'
                              }`}
                              onClick={() => atualizarLinha(linha.id, { tipoFibra: 'TT' })}
                              disabled={isReadOnly}
                            >
                              TT
                            </Button>
                          </div>
                        </div>
                      ) : linha.tipo === 'LINHA_NOVA' ? (
                        // Linha NOVA: sem numero (definido na ativacao). So escolhe o DDD. (Douglas 25/06)
                        <div className="flex items-center gap-2 flex-1">
                          <span className="shrink-0 text-xs font-medium text-foreground">Linha Nova</span>
                          <Select
                            value={onlyDigits(linha.numero || '').length === 2 ? onlyDigits(linha.numero) : ''}
                            onValueChange={(ddd) => atualizarLinha(linha.id, { numero: ddd })}
                            disabled={isReadOnly}
                          >
                            <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue placeholder="DDD" /></SelectTrigger>
                            <SelectContent className="max-h-60">
                              {['11','12','13','14','15','16','17','18','19','21','22','24','27','28','31','32','33','34','35','37','38','41','42','43','44','45','46','47','48','49','51','53','54','55','61','62','63','64','65','66','67','68','69','71','73','74','75','77','79','81','82','83','84','85','86','87','88','89','91','92','93','94','95','96','97','98','99'].map(d => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <span className="text-[10px] text-muted-foreground shrink-0">nº definido na ativação</span>
                        </div>
                      ) : isTT || linha.tipo === 'RENOVACAO' || linha.tipo === 'PORTABILIDADE' ? (
                        (() => {
                          const numerosUsados = (linha.tipo === 'TT' || linha.tipo === 'TT_PRE')
                            ? numerosUsadosTT 
                            : linha.tipo === 'PORTABILIDADE' 
                              ? numerosUsadosPortabilidade 
                              : numerosUsadosRenovacao;
                          const numerosParaEstaLinha = numerosDisponiveis.filter(
                            n => n === linha.numero || !numerosUsados.includes(n)
                          );
                          const temSugestoes = (isTT || linha.tipo === 'RENOVACAO' || linha.tipo === 'PORTABILIDADE') && numerosParaEstaLinha.length > 0;
                          
                          const valorAtualInput = (linha.numero === 'TT' || linha.numero === 'Renovação') 
                            ? '' 
                            : linha.numero;
                          
                          const validarFormatoTelefone = (numero: string): { valido: boolean; mensagem?: string } => {
                            if (!numero || numero === 'TT' || numero === 'Renovação') {
                              return { valido: true };
                            }
                            const digits = onlyDigits(numero);
                            if (digits.length < 10 || digits.length > 13) {
                              return { valido: false, mensagem: 'Formato inválido (10-11 dígitos)' };
                            }
                            return { valido: true };
                          };
                          
                          const validacao = validarFormatoTelefone(valorAtualInput);
                          
                          return (
                            <div className="flex flex-col gap-1 flex-1" title={travada ? motivoTrava(linha) : undefined}>
                              <div className="flex items-center gap-1.5">
                                {/* ⛔ O número NÃO vira texto só porque chegou a 10 dígitos
                                    (Douglas 01/09/2026). O campo sobe sozinho após 600ms parado,
                                    então qualquer hesitação no meio do número fechava esta
                                    condição, trocava o input por um <span> e MATAVA o cursor —
                                    com o telefone pela metade e sem como corrigir. Texto fixo
                                    só para linha travada (item já finalizado). */}
                                {linha.tipo === 'RENOVACAO' && travada && validacao.valido && onlyDigits(valorAtualInput).length >= 10 ? (
                                  <span className="flex-1 text-[14.5px] font-bold tabular-nums text-foreground">{formatarTelefoneLocal(valorAtualInput)}</span>
                                ) : (
                                <InputTelefoneLinha
                                  valor={valorAtualInput}
                                  onCommit={(valorDigitado) => {
                                    const apenasDigitos = onlyDigits(valorDigitado);
                                    if (apenasDigitos.length >= 10) {
                                      const normalizado = apenasDigitos.startsWith('55') && apenasDigitos.length >= 12
                                        ? apenasDigitos.substring(2)
                                        : apenasDigitos;
                                      atualizarLinha(linha.id, { numero: normalizado });
                                      return;
                                    }
                                    if (valorDigitado.includes('(') || valorDigitado.includes('-')) {
                                      atualizarLinha(linha.id, { numero: apenasDigitos });
                                    } else {
                                      atualizarLinha(linha.id, { numero: valorDigitado });
                                    }
                                  }}
                                  className="h-8 text-xs font-mono flex-1"
                                  disabled={isReadOnly || travada}
                                />
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 shrink-0"
                                  title="Copiar número"
                                  onClick={() => {
                                    if (!valorAtualInput) return;
                                    navigator.clipboard?.writeText(valorAtualInput)
                                      .then(() => toast.success('Número copiado'))
                                      .catch(() => toast.error('Não foi possível copiar'));
                                  }}
                                >
                                  <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>

                                {temSugestoes && (
                                  <Select
                                    value={linha.numero}
                                    onValueChange={(value) => {
                                      const dadosFatura = linhasDetalhadas?.find(l => 
                                        formatarTelefoneLocal(l.numero) === value
                                      );
                                      atualizarLinha(linha.id, { 
                                        numero: value,
                                        gbAtual: dadosFatura?.gbAtual,
                                        valorAtual: dadosFatura?.valorAtual,
                                        planoAtual: dadosFatura?.planoAtual,
                                      });
                                    }}
                                    disabled={isReadOnly || travada}
                                  >
                                    <SelectTrigger className="h-8 w-8 p-0 border-0 bg-transparent">
                                      <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {numerosParaEstaLinha.map((num) => {
                                        const dadosFatura = linhasDetalhadas?.find(l => 
                                          formatarTelefoneLocal(l.numero) === num
                                        );
                                        return (
                                          <SelectItem key={num} value={num}>
                                            <div className="flex items-center gap-2">
                                              <span className="font-mono text-xs">{num}</span>
                                              {dadosFatura && (
                                                <span className="text-[10px] text-muted-foreground">
                                                  {dadosFatura.gbAtual}GB • {formatarMoedaPlano(dadosFatura.valorAtual)}
                                                </span>
                                              )}
                                            </div>
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                )}
                                
                              </div>
                              
                              {!validacao.valido && valorAtualInput && (
                                <span className="text-xs text-warning flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {validacao.mensagem}
                                </span>
                              )}
                              <SeloOperadoraLinha tipo={linha.tipo} numero={linha.numero}
                                porTelefone={operadorasLinhas} consultando={consultandoOperadora} />
                            </div>
                          );
                        })()
                      ) : (
                        <span className={`text-sm font-medium ${cores.text}`}>
                          {linha.numero}
                        </span>
                      )}
                    </div>

                    {/* Licenças: a MESMA opção N vezes vira quantidade, não N linhas
                        repetidas (Douglas 05/08: "não repetir mil vezes o serviço"). */}
                    {linha.tipo === 'SERVICO' && (
                      <div className="flex items-center gap-1 shrink-0" title={travada ? motivoTrava(linha) : undefined}>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{linha.categoriaCustom === CATEGORIA_M2M ? 'Linhas' : 'Lic.'}</span>
                        <Input
                          type="number"
                          min={1}
                          value={linha.quantidade ?? 1}
                          onChange={(e) => {
                            const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                            if (n > 1 && linhaEhServicoDigital(linha)) {
                              toast.info('Um de cada serviço por linha', { description: MENSAGEM_SERVICO_NA_LINHA });
                              return;
                            }
                            atualizarLinha(linha.id, { quantidade: n });
                          }}
                          className="h-8 w-16 text-xs text-center tabular-nums"
                          disabled={isReadOnly || travada}
                        />
                      </div>
                    )}

                    {/* Seletor de plano (RENOVACAO usa o select rotulado no corpo) */}
                    <div
                      className={linha.tipo === 'RENOVACAO' ? 'hidden' : ehCpf ? 'w-[22rem] max-w-full' : 'w-44'}
                      title={travada ? motivoTrava(linha) : undefined}
                    >
                      <Select
                        value={planosDisponiveis.some((p) => p.id === linha.planoId) ? (linha.planoId || '') : ''}
                        onValueChange={(value) => atualizarLinha(linha.id, { planoId: value })}
                        disabled={isReadOnly || travada}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder={linha.planoId && !planosDisponiveis.some((p) => p.id === linha.planoId) ? `Plano fora da tabela ${ehCpf ? "CPF" : "CNPJ"} — escolha outro` : isFibra ? "Velocidade" : linha.tipo === 'TV' ? "Pacote TV" : "Plano"} />
                        </SelectTrigger>
                        <SelectContent>
                          {planosDisponiveis.map((plano) => (
                            <SelectItem key={plano.id} value={plano.id}>
                              <div className="flex items-center justify-between gap-2">
                                <span>{ehCpf || linha.tipo === 'RENOVACAO' || linha.tipo === 'SERVICO' || linha.categoriaCustom ? plano.nome :isFibra ? (plano.velocidade ?? plano.nome) : linha.tipo === 'TV' ? plano.nome.replace('Claro TV', '').trim() : plano.gb === 0 ? '0GB (Voz)' : `${plano.gb}GB`}</span>
                                <span className="text-muted-foreground">
                                  {formatarMoedaPlano(plano.valor)}{promoPlanoCpf(plano)}{pracaPlanoCpf(plano)}{plano.maxLinhasBan ? ` · máx ${plano.maxLinhasBan} lin/BAN` : ''}
                                </span>
                                {plano.destaque && (
                                  <Star className="w-3 h-3 text-warning" />
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Valor — serviço digital multiplica pelas licenças da linha */}
                    {linha.plano && (
                      <div className="text-right shrink-0">
                        <span className="font-display text-sm font-bold tabular-nums text-[#2E2019] dark:text-[#E8DEDE]">
                          {formatarMoedaPlano(linha.tipo === 'RENOVACAO' && comparativo
                            ? comparativo.valorNovo
                            :
                            linha.plano.valor * (linha.tipo === 'SERVICO' ? (linha.quantidade ?? 1) : 1) +
                            (linha.adicionais ? Object.entries(linha.adicionais).reduce((sum, [adicId, qtd]) => {
                              const adicTV = ADICIONAIS_TV.find(a => a.id === adicId);
                              const adicFibra = ADICIONAIS_FIBRA.find(a => a.id === adicId);
                              return sum + ((adicTV?.valor || adicFibra?.valor || 0) * (qtd || 0));
                            }, 0) : 0)
                          )}
                        </span>
                        {linha.tipo === 'SERVICO' && (linha.quantidade ?? 1) > 1 && (
                          <div className="text-[10px] text-muted-foreground tabular-nums">
                            {linha.quantidade}× {formatarMoedaPlano(linha.plano.valor)}
                          </div>
                        )}
                      </div>
                    )}

                    {renderAcaoFicaCancelada(linha, false)}
                    {/* Remover */}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={() => removerLinha(linha.id)}
                      disabled={isReadOnly || travada || isLinhaVinculada(linha)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  {linha.tipo === 'RENOVACAO' && comparativo && (() => {
                    // Antes → depois (Douglas 02/10/2026): o plano de hoje fica
                    // apagado à esquerda e o plano novo, que é o próprio seletor,
                    // em destaque à direita. Embaixo, o resultado da troca.
                    const deltaReais = linha.planoId && comparativo.valorAtual > 0 ? comparativo.valorNovo - comparativo.valorAtual : null;
                    const gbDelta = linha.plano?.gb != null && linha.gbAtual != null ? Number(linha.plano.gb) - Number(linha.gbAtual) : null;
                    const faixaLinha = linha.planoId ? faixaDaLinhaRen(comparativo.delta) : null;
                    const pctLinha = faixaLinha !== null ? matrizRen?.pct.get(`1000:${faixaLinha}`) ?? null : null;
                    const comLinha = pctLinha !== null && comparativo.valorNovo > 0 ? comparativo.valorNovo * (pctLinha / 100) : null;
                    const tmLinha = tmPelaFidelidade(fidelidadeDaLinha(linha.numero));
                    const corDelta = deltaReais !== null && deltaReais > 0
                      ? 'text-[#075E54] dark:text-[#25D366]'
                      : deltaReais !== null && deltaReais < 0 ? 'text-primary dark:text-[#C87484]' : 'text-foreground';
                    const deltaPct = comparativo.delta === null ? null : `${comparativo.delta >= 0 ? '+' : ''}${comparativo.delta.toFixed(1).replace('.', ',')}%`;
                    return (
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[minmax(0,1fr)_20px_minmax(0,1.25fr)] sm:items-stretch">
                          <div className="min-w-0 rounded-lg border border-border px-3 py-1.5">
                            <div className="text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground/70">Hoje · fatura</div>
                            <div className="truncate text-[13px] font-semibold text-muted-foreground" title={linha.planoAtual || ''}>
                              {linha.planoAtual || 'Não informado na fatura'}
                            </div>
                            <div className="text-[11px] tabular-nums text-muted-foreground">
                              {linha.gbAtual ? `${linha.gbAtual} GB · ` : ''}{comparativo.valorAtual > 0 ? formatarMoedaPlano(comparativo.valorAtual) : 'R$ —'}
                            </div>
                          </div>
                          <ArrowRight className="hidden h-4 w-4 self-center text-muted-foreground sm:block" />
                          <div className="min-w-0 rounded-lg border-[1.5px] border-[#9D2530] bg-[#FBF1F2] px-3 py-1.5 dark:bg-transparent">
                            <div className="text-[8.5px] font-bold uppercase tracking-[1.1px] text-[#7A2235] dark:text-[#C87484]">Plano novo</div>
                            <Select
                              value={linha.planoId || ''}
                              onValueChange={(value) => atualizarLinha(linha.id, { planoId: value })}
                              disabled={isReadOnly || travada}
                            >
                              <SelectTrigger className="h-7 border-0 bg-transparent px-0 text-[14px] font-bold shadow-none focus:ring-0">
                                <SelectValue placeholder="Escolha o plano novo" />
                              </SelectTrigger>
                              <SelectContent>
                                {planosDisponiveis.map((plano) => (
                                  <SelectItem key={plano.id} value={plano.id}>
                                    {plano.nome} · {formatarMoedaPlano(plano.valor)}/mês{promoPlanoCpf(plano)}{pracaPlanoCpf(plano)}{plano.maxLinhasBan ? ` · máx ${plano.maxLinhasBan} lin/BAN` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="text-[11px] tabular-nums text-muted-foreground">
                              {linha.planoId ? `${linha.plano?.gb != null ? `${linha.plano.gb} GB · ` : ''}${formatarMoedaPlano(comparativo.valorNovo)}` : 'a sugestão da fatura entra pelo botão Aplicar nas linhas'}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3 sm:gap-px">
                          <div className="flex min-w-0 items-baseline gap-2 whitespace-nowrap bg-[#FAF8F4] px-3 py-1.5 dark:bg-[#241A1A]" title="TM da linha pela fidelidade na conta (fim da fidelidade − 24 meses)">
                            <span className="text-[9px] font-bold uppercase tracking-[1px] text-muted-foreground">TM</span>
                            <b className="font-display text-[13.5px] tabular-nums">{tmLinha != null ? `${tmLinha} m` : '—'}</b>
                            <span className="flex min-w-0 items-center gap-1 truncate text-[11px] text-muted-foreground">
                              <span aria-hidden className={`h-2 w-2 flex-shrink-0 rounded-full ${tmLinha == null ? 'border-[1.5px] border-[#A6907B]' : tmLinha >= 18 ? 'bg-[#128C7E] dark:bg-[#25D366]' : tmLinha >= 12 ? 'bg-[#9D2530] dark:bg-[#C87484]' : 'border-[1.5px] border-[#9D2530] dark:border-[#C87484]'}`} />
                              {tmLinha == null ? 'sem fidelidade lida' : tmLinha >= 18 ? 'livre' : tmLinha >= 12 ? 'exige aparelho' : 'só bloco'}
                            </span>
                          </div>
                          <div className="flex min-w-0 items-baseline gap-2 whitespace-nowrap bg-[#FAF8F4] px-3 py-1.5 dark:bg-[#241A1A]">
                            <span className="text-[9px] font-bold uppercase tracking-[1px] text-muted-foreground">Delta</span>
                            <b className={`font-display text-[13.5px] tabular-nums ${corDelta}`}>
                              {deltaReais === null ? '—' : `${deltaReais > 0 ? '+' : '−'}${formatarMoedaPlano(Math.abs(deltaReais))}`}
                            </b>
                            <span className="truncate text-[11px] tabular-nums text-muted-foreground">
                              {[deltaPct, gbDelta !== null && gbDelta !== 0 ? `${gbDelta > 0 ? '+' : ''}${gbDelta} GB` : null].filter(Boolean).join(' · ')}
                            </span>
                          </div>
                          <div
                            className={`flex min-w-0 items-baseline gap-2 whitespace-nowrap px-3 py-1.5 ${faixaLinha === 1 ? 'bg-[#D7EEE6] dark:bg-[#10261F]' : 'bg-[#FAF8F4] dark:bg-[#241A1A]'}`}
                            title="Faixa pelo delta da linha · % da tabela de renovação na faixa de faturamento R$ 1.000 (a real é a do seu fechamento)"
                          >
                            <span className="text-[9px] font-bold uppercase tracking-[1px] text-muted-foreground">Faixa</span>
                            <b className={`font-display text-[13.5px] tabular-nums ${faixaLinha === 1 ? 'text-[#075E54] dark:text-[#25D366]' : 'text-foreground'}`}>
                              {faixaLinha !== null ? `F${faixaLinha}${pctLinha !== null ? ` · ${pctLinha}%` : ''}` : '—'}
                            </b>
                            {comLinha !== null && (
                              <span className="truncate text-[11px] tabular-nums text-muted-foreground">{formatarMoedaPlano(comLinha)} com.</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-[3px]" aria-label={faixaLinha !== null ? `Faixa F${faixaLinha} de 7` : 'Faixa ainda não calculada'}>
                          {[1, 2, 3, 4, 5, 6, 7].map((f) => (
                            <span
                              key={f}
                              className={`rounded px-1.5 py-px font-display text-[10px] font-bold ${f === faixaLinha
                                ? (f === 1 ? 'bg-[#128C7E] text-white dark:bg-[#25D366] dark:text-[#10261F]' : 'bg-[#9D2530] text-white')
                                : 'bg-[#EDE8E0] text-[#A6907B] dark:bg-[#161010] dark:text-[#8A7576]'}`}
                            >
                              F{f}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Benefícios do plano selecionado */}
                  {linha.plano && !isFibra && linha.tipo !== 'TV' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
                      {linha.tipo === 'RENOVACAO' && <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Benefícios</span>}
                      <BeneficiosPlano plano={linha.plano} compact chips={linha.tipo === 'RENOVACAO'} />
                    </div>
                  )}
                  {linha.tipo === 'RENOVACAO' && avisoTmRenovacao && linha.id === primeiraRenovacaoId && (
                    <p className={`mt-2 rounded-md px-2.5 py-1.5 text-[11.5px] ${avisoTmRenovacao.ok ? 'text-[#075E54] dark:text-[#25D366]' : 'bg-[#F3E9EA] text-[#7A2235] dark:bg-[#2A1A1D] dark:text-[#C87484]'}`}>
                      {avisoTmRenovacao.texto}
                    </p>
                  )}

                  {/* FAMÍLIA CPF: titular Pós com dependentes (Douglas 29/09/2026) */}
                  {ehCpf && TIPOS_DEPENDENTE.includes(linha.tipo) && linha.plano && (() => {
                    if (linha.titularLinhaId) {
                      const titular = linhas.find((l) => chaveLinha(l) === linha.titularLinhaId);
                      const dep = linha.plano?.dependente;
                      return (
                        <div className="mt-2 flex items-center gap-2 border-t pt-2 text-[11px]">
                          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">Dependente</span>
                          <span className="min-w-0 truncate text-muted-foreground" title={linha.plano?.nome}>
                            divide a internet de {titular ? telefoneCurto(titular) : 'um titular que saiu da proposta'}
                            {' · '}{dep === 'incluso' ? 'incluído no plano, sem custo' : dep === 'dados' ? 'só dados' : 'pago'}
                          </span>
                          <div className="ml-auto flex shrink-0 items-center gap-1.5">
                            <Select
                              value={linha.tipo === 'LINHA_NOVA' ? 'LINHA_NOVA' : linha.tipo === 'PORTABILIDADE' ? 'PORTABILIDADE' : ''}
                              onValueChange={(v) => trocarOperacaoDependente(linha, v as TipoLinha)}
                              disabled={isReadOnly || travada}
                            >
                              <SelectTrigger className="h-6 w-auto gap-1 whitespace-nowrap px-2 text-[11px]" title="Operação do dependente">
                                <SelectValue placeholder={getTipoLinhaLabel(linha.tipo).label} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PORTABILIDADE" className="text-xs">Portabilidade</SelectItem>
                                <SelectItem value="LINHA_NOVA" className="text-xs">Linha nova</SelectItem>
                              </SelectContent>
                            </Select>
                            <Select
                              value={dep === 'dados' ? 'dados' : 'voz'}
                              onValueChange={(v) => trocarTipoDependente(linha, v as 'voz' | 'dados')}
                              disabled={isReadOnly || travada}
                            >
                              <SelectTrigger className="h-6 w-auto gap-1 whitespace-nowrap px-2 text-[11px]" title="Tipo do dependente">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="voz" className="text-xs">
                                  Voz + dados · {formatarMoedaPlano(Number(planoDoDependente(linha, 'pago')?.valor ?? 55))}
                                </SelectItem>
                                <SelectItem value="dados" className="text-xs" disabled={!planoDoDependente(linha, 'dados')}>
                                  Só dados · {formatarMoedaPlano(Number(planoDoDependente(linha, 'dados')?.valor ?? 29.99))}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          <button
                            type="button"
                            onClick={() => tornarTitular(linha)}
                            disabled={isReadOnly || travada}
                            className="shrink-0 whitespace-nowrap rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:border-border"
                          >
                            Tornar titular
                          </button>
                          </div>
                        </div>
                      );
                    }
                    const meta = metaDoPlano(linha.plano);
                    const deps = dependentesDaLinha(linha);
                    const vagas = vagasDoTitular(meta, deps.length);
                    const outrosPos = titularesPos.filter((t) => t.id !== linha.id && vagasDoTitular(metaDoPlano(t.plano), dependentesDaLinha(t).length).total > 0);
                    if (meta?.familia_movel !== 'POS' && !outrosPos.length) return null;
                    const inclusos = Number(meta?.dependentes_inclusos ?? 0);
                    const maximo = inclusos + Number(meta?.dependentes_pagos_max ?? 0);
                    return (
                      <div className="mt-2 flex items-center gap-2 border-t pt-2 text-[11px]">
                        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">Família</span>
                        <span className="min-w-0 truncate text-muted-foreground">
                          {meta?.familia_movel === 'POS'
                            ? `${deps.length} de ${maximo} dependente(s) · ${inclusos} incluído(s) no plano, os demais ${formatarMoedaPlano(Number(planoDoDependente(linha, 'pago')?.valor ?? 55))}`
                            : 'Controle não leva dependente'}
                        </span>
                        <div className="ml-auto flex shrink-0 items-center gap-1.5">
                          {!deps.length && outrosPos.length > 0 && (
                            <Select value="" onValueChange={(v) => tornarDependente(linha, v)} disabled={isReadOnly || travada}>
                              <SelectTrigger className="h-6 w-auto gap-1 whitespace-nowrap px-2 text-[11px]">
                                <SelectValue placeholder="Tornar dependente de…" />
                              </SelectTrigger>
                              <SelectContent>
                                {outrosPos.map((t) => (
                                  <SelectItem key={t.id} value={chaveLinha(t)} className="text-xs">
                                    {telefoneCurto(t)} · {t.plano?.nome}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {meta?.familia_movel === 'POS' && (
                            <button
                              type="button"
                              onClick={() => adicionarDependente(linha)}
                              disabled={isReadOnly || travada || vagas.total <= 0}
                              title={vagas.total <= 0 ? 'Este Pós já está com todos os dependentes' : vagas.inclusos > 0 ? 'Entra incluído no plano, sem custo' : 'Entra como dependente pago'}
                              className="whitespace-nowrap rounded-md border border-border px-2 py-0.5 text-[11px] font-semibold text-foreground hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:border-border"
                            >
                              + Dependente
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* LEVA (Douglas 02/10/2026): Passaport, serviço digital e
                      aparelho numa fileira só. O que a linha já leva aparece como
                      etiqueta; cada "+" abre a mesma lista de antes. O aparelho
                      continua virando item separado no pedido. */}
                  {(linha.tipo === 'TT' || linha.tipo === 'TT_PRE' || linha.tipo === 'PORTABILIDADE' ||
                    linha.tipo === 'NOVA' || linha.tipo === 'RENOVACAO' ||
                    linha.tipo === 'LINHA_NOVA') && linha.planoId && (() => {
                    const bloqueado = isReadOnly || travada;
                    const doLinha = servicosDaLinha(linha);
                    const ativos = new Set(doLinha.map((l) => l.planoId));
                    const passaportsAtivos = ADICIONAIS_MOVEL.filter((a) => (linha.adicionais?.[a.id] || 0) > 0);
                    const legados = aparelhosLegadosDaLinha(linha);
                    const CHIP_ON = 'inline-flex items-center gap-1 rounded-full bg-[#F3E9EA] px-2.5 py-0.5 text-[11px] font-semibold text-[#7A2235] dark:bg-[#2A1A1D] dark:text-[#C87484]';
                    const CHIP_ADD = 'rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:opacity-50';
                    const seletorAparelho = (
                      <AparelhoSelectorLinha
                        linha={linha}
                        disabled={bloqueado}
                        onChange={(aparelho) => atualizarLinha(linha.id, { aparelho })}
                        tipoPessoa={tipoPessoa}
                        compacto
                      />
                    );
                    return (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t pt-2" title={travada ? motivoTrava(linha) : undefined}>
                        <span className="mr-1 text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Leva</span>

                        {passaportsAtivos.map((adicional) => (
                          <span key={adicional.id} className={CHIP_ON} title={`+R$ ${adicional.valor.toFixed(2).replace('.', ',')} /mês`}>
                            {adicional.nome}
                            <button type="button" disabled={bloqueado} onClick={() => ajustarQuantidadeAdicional(linha.id, adicional.id, -1)}
                              aria-label={`Tirar ${adicional.nome}`} className="leading-none disabled:opacity-50">×</button>
                          </span>
                        ))}

                        {doLinha.map((servico) => (
                          <span key={servico.id} className={CHIP_ON}>
                            {servico.plano?.nome ?? 'Serviço'}
                            <button type="button" disabled={bloqueado} onClick={() => ajustarLicencas(servico.id, -1)}
                              title="Menos uma licença" className="px-0.5 leading-none disabled:opacity-50">−</button>
                            <span className="tabular-nums">{servico.quantidade ?? 1}</span>
                            <button type="button" disabled={bloqueado} onClick={() => ajustarLicencas(servico.id, 1)}
                              title="Mais uma licença" className="px-0.5 leading-none disabled:opacity-50">+</button>
                          </span>
                        ))}

                        {legados.map((aparelho) => (
                          <span key={aparelho.id} className={CHIP_ON}>
                            {aparelho.plano?.nome ?? aparelho.planoNovo ?? 'Aparelho'} · 1 unidade
                          </span>
                        ))}

                        {linha.aparelho && seletorAparelho}

                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" disabled={bloqueado} className={CHIP_ADD}>+ Passaport</button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-64 p-1.5">
                            {ADICIONAIS_MOVEL.map((adicional) => {
                              const ativo = (linha.adicionais?.[adicional.id] || 0) > 0;
                              return (
                                <button key={adicional.id} type="button" disabled={bloqueado}
                                  onClick={() => ajustarQuantidadeAdicional(linha.id, adicional.id, ativo ? -1 : 1)}
                                  className={cn('flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                                    ativo && 'font-semibold text-[#7A2235] dark:text-[#C87484]')}>
                                  <span>{ativo ? '✓ ' : ''}{adicional.nome}</span>
                                  <span className="tabular-nums text-muted-foreground">+{formatarMoedaPlano(adicional.valor)}</span>
                                </button>
                              );
                            })}
                          </PopoverContent>
                        </Popover>

                        {opcoesServicoDigital.length > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" disabled={bloqueado} className={CHIP_ADD}>+ Serviço</button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-72 p-2">
                              <SeletorServicoDigital
                                opcoes={opcoesServicoDigital}
                                ativos={ativos}
                                nomeCategoria={(cat) => tiposByCategoria.get(cat)?.nome ?? labelCategoria(cat)}
                                onEscolher={(o) => alternarServicoNaLinha(linha, o.categoria, o.plano)}
                              />
                            </PopoverContent>
                          </Popover>
                        )}

                        {!linha.aparelho && legados.length === 0 && seletorAparelho}
                      </div>
                    );
                  })()}

                  {/* Adicionais de FIBRA */}
                  {linha.tipo === 'FIBRA' && linha.planoId && ehCpf && renderAdicionaisCpf(linha, ADICIONAIS_FIBRA, 'Adicionais:')}
                    {linha.tipo === 'FIBRA' && linha.planoId && !ehCpf && (() => {
                    const qtdClaroFixo = linha.adicionais?.['fibra-claro-fixo'] || 0;
                    const hasMesh = (linha.adicionais?.['fibra-mesh-2'] || 0) > 0;
                    const qtdMeshAdicional = linha.adicionais?.['fibra-mesh-adicional'] || 0;
                    
                    return (
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">Adicionais:</span>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  disabled={isReadOnly || travada}
                                  onClick={() => ajustarQuantidadeAdicional(linha.id, 'fibra-claro-fixo', 1)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (travada) return;
                                    if (qtdClaroFixo > 0) {
                                      ajustarQuantidadeAdicional(linha.id, 'fibra-claro-fixo', -1);
                                    }
                                  }}
                                  className={cn(
                                    "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                    qtdClaroFixo > 0 
                                      ? "bg-danger/10 border-danger text-danger font-medium" 
                                      : "bg-muted/50 border-border text-muted-foreground hover:border-danger"
                                  )}
                                >
                                  Claro Fixo {qtdClaroFixo > 0 && `(${qtdClaroFixo})`}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>+R$ 10,00/linha | Clique: +1 | Direito: -1</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  disabled={isReadOnly || travada}
                                  onClick={() => ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-2', hasMesh ? -1 : 1)}
                                  className={cn(
                                    "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                    hasMesh
                                      ? "bg-info/10 border-info text-info font-medium"
                                      : "bg-muted/50 border-border text-muted-foreground hover:border-info"
                                  )}
                                >
                                  Mesh 2x
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>+R$ 30,00/mês</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {hasMesh && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (qtdMeshAdicional < 2) {
                                        ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-adicional', 1);
                                      }
                                    }}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (qtdMeshAdicional > 0) {
                                        ajustarQuantidadeAdicional(linha.id, 'fibra-mesh-adicional', -1);
                                      }
                                    }}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                      qtdMeshAdicional > 0 
                                        ? "bg-info/10 border-info text-info font-medium" 
                                        : "bg-muted/50 border-border text-muted-foreground hover:border-info"
                                    )}
                                  >
                                    +Mesh {qtdMeshAdicional > 0 && `(${qtdMeshAdicional})`}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>+R$ 15,00/cada - máx 2</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Adicionais de TV */}
                  {linha.tipo === 'TV' && linha.planoId && ehCpf && renderAdicionaisCpf(linha, ADICIONAIS_TV, 'Adicionais da TV:')}
                    {linha.tipo === 'TV' && linha.planoId && !ehCpf && (() => {
                    const qtdPonto = linha.adicionais?.['tv-ponto'] || 0;
                    const qtdPonto4k = linha.adicionais?.['tv-ponto-4k'] || 0;
                    
                    return (
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">📺 Pontos:</span>
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => ajustarQuantidadeAdicional(linha.id, 'tv-ponto', 1)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (qtdPonto > 0) {
                                      ajustarQuantidadeAdicional(linha.id, 'tv-ponto', -1);
                                    }
                                  }}
                                  className={cn(
                                    "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                    qtdPonto > 0 
                                      ? "bg-primary/[0.10] border-primary/40 text-primary dark:text-foreground font-medium" 
                                      : "bg-muted/50 border-border text-muted-foreground hover:border-primary/40"
                                  )}
                                >
                                  Adicional {qtdPonto > 0 && `(${qtdPonto})`}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>+R$ 29,90/cada | Clique: +1 | Direito: -1</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          
                          {linha.planoId === 'tv-4k' && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={() => ajustarQuantidadeAdicional(linha.id, 'tv-ponto-4k', 1)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      if (qtdPonto4k > 0) {
                                        ajustarQuantidadeAdicional(linha.id, 'tv-ponto-4k', -1);
                                      }
                                    }}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full border transition-colors",
                                      qtdPonto4k > 0 
                                        ? "bg-warning/10 border-warning text-warning font-medium" 
                                        : "bg-muted/50 border-border text-muted-foreground hover:border-warning"
                                    )}
                                  >
                                    4K {qtdPonto4k > 0 && `(${qtdPonto4k})`}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>+R$ 39,90/cada | Clique: +1 | Direito: -1</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

    </div>
  );
});
